import { languageRule } from './language-rule'
import type {
  AiTurnTelemetry,
  CompanionConfig,
  CompanionState,
  Message,
  CompanionPersonaId,
  LiveSpeech,
  Pending,
  PendingKind,
  ToolDelivery,
  TurnAccess,
  TurnReadingWire,
} from '../types/companion'
import type {
  ApiMessage,
  ToolCall,
  ToolResult,
  StreamResult,
  ToolDefinition,
  CompanionDebugRound,
} from '../types/api'
import { EventBus } from './EventBus'
import { ContextCollector } from './ContextCollector'
import { captureScreen, measureCapture, type CaptureResult } from './ScreenCapture'
import { parsePointTag } from './PointParser'
import { drainFramesFromBuffer, parseSseFrame } from './sse-parse'
import { appendToolContext, appendGroundingContext } from '../knowledge/promptAssembler'
import {
  collectPanelLayout,
  collectLabeledElements,
  describePanelLayout,
  describeLabeledElements,
  inferPanelTypeFromText,
} from './PanelCollector'
import { annotateScreenshot } from './ScreenAnnotator'
import { mousePosition } from './MouseTracker'
import {
  getToolsForContext,
  getToolIndex,
  getIndexedToolNames,
  getRegisteredScope,
  getToolDefinition,
  getToolManifest,
  terminalPassthrough,
  TERMINAL_TOOLS,
  toolEffect,
} from '../tools/tool-registry'
import type { PageContext } from '../tools/tool-registry'
import {
  LOADED_THROUGH_USE_TOOL,
  USE_TOOL,
  fixedRoundTools,
  parseToolDelivery,
  unwrapUseTool,
  type RefusedCall,
} from '../tools/tool-delivery'
import { isResearchAndActRequest } from './data-question'
import { missingNamedPanels, missingPanelSection } from './named-panels'
import { analystPending, askedBy, pendingWire, restoredPending } from './pending'

const DEFAULT_MAX_HISTORY = 10
const INCOMPLETE_RESPONSE_MESSAGE = "I couldn't complete that response. Please try again."
const PENDING_STOPPED = 'That took more steps than I can run in one go, so I stopped partway.'
const PENDING_CONTINUE = "Ask me to continue and I'll pick it up from here."
const PENDING_RESPONSE_MESSAGE = `${PENDING_STOPPED} ${PENDING_CONTINUE}`
const RESEARCH_FAILURE_MESSAGE =
  'The research step failed, so I stopped before making any strategy or backtest changes.'

/**
 * The calls that changed the trader's workspace and succeeded: their effect is undoable or
 * asks first. A view change (navigate_to, set_focus_date) saves nothing, so it is not one.
 */
function appliedChanges(calls: readonly ToolCall[], results: readonly ToolResult[]): ToolCall[] {
  const succeeded = new Set(results.filter((r) => r.success).map((r) => r.tool_use_id))
  return calls.filter((call) => {
    if (!succeeded.has(call.tool_use_id) || !getToolManifest(call.name)) return false
    const effect = toolEffect(call)
    return effect === 'undoable' || effect === 'confirm'
  })
}

/** Where changes landed, in words: the panel types (or the shared capability) their tools name. */
function changedPlaces(calls: readonly ToolCall[]): string {
  const places = [
    ...new Set(
      calls.flatMap((call) => {
        const m = getToolManifest(call.name)
        if (m?.panels.length) return m.panels.map((p) => p.replace(/[-_]/g, ' '))
        return [m?.capability ? m.capability.replace(/_/g, ' ') : 'workspace']
      }),
    ),
  ]
  const last = places.pop()
  return `your ${places.length ? `${places.join(', ')} and ${last}` : last}`
}

/**
 * The closing text of a turn that used every round while its last round still made calls
 * (2026-10-20: a line drawn by the eighth round read "stopped partway" beside its Undo and
 * Keep). A last round that changed something left the change in place, so the text says
 * so, after what the round said it was doing; a change from an earlier round is named
 * beside the stop. With no change applied, the turn did stop partway.
 */
function cappedTurnText(
  lastRound: { text: string; calls: readonly ToolCall[]; results: readonly ToolResult[] },
  turn: { calls: readonly ToolCall[]; results: readonly ToolResult[] },
): string {
  const applied = appliedChanges(turn.calls, turn.results)
  if (applied.length === 0) return PENDING_RESPONSE_MESSAGE
  const where = changedPlaces(applied)
  const done =
    applied.length === 1
      ? `The change to ${where} is applied.`
      : `The ${applied.length} changes to ${where} are applied.`
  if (appliedChanges(lastRound.calls, lastRound.results).length === 0)
    return `${PENDING_STOPPED} ${done} ${PENDING_CONTINUE}`
  const said = lastRound.text.trim()
  return `${said ? `${said}\n\n` : ''}${done} Ask me to continue if there was more to do.`
}

function currentDateContext(now = new Date()): string {
  const date = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
  return `## current date\n${date} (America/New_York). Treat this date as authoritative for past/future judgments.`
}

interface TurnContext {
  id: string
  signal: AbortSignal
}

/** What the local classifier read in a message (classifyIntent). */
interface TurnClassification {
  panel: string | null
  needs_image: boolean
  needs_actions: boolean
  intent?: string | null
  looks_howto?: boolean
}

function relayAbortSignal(
  signal: AbortSignal | undefined,
  controller: AbortController,
): () => void {
  if (!signal) return () => {}
  const abort = () => controller.abort()
  if (signal.aborted) {
    controller.abort()
    return () => {}
  }
  signal.addEventListener('abort', abort, { once: true })
  return () => signal.removeEventListener('abort', abort)
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

/**
 * Central state machine for the companion.
 *
 * States: idle → listening → processing → responding → idle
 *
 * Orchestrates: context collection, screen capture, prompt assembly,
 * Claude API streaming, point parsing, and conversation history.
 */
/** Monotonic counter so each manager instance gets a stable conversation id
 *  without pulling in crypto/Date (keeps the package deterministic in tests). */
let companionConversationSeq = 0

/**
 * The voice path's spoken-answer style (overridable via
 * CompanionConfig.voiceStyle). Deliberately NOT a word/sentence quota — a
 * hard cap clips the substance. The contract is salience-first conciseness:
 * everything that matters, nothing that doesn't. A spoken turn's prompt is
 * chat's, with this in place of the cashtag rule (buildTurnPrompt).
 */
export const DEFAULT_SPOKEN_STYLE = `## spoken answer
you are SPEAKING this answer aloud — every extra word costs the listener time.
prioritize substance over coverage: lead with the single most important fact or number, add only what changes the listener's understanding, then stop. no preamble, no restating the question, no hedging, no recap of what you just said.
plain conversational speech: no markdown, no lists, no tables; round numbers the way a person would say them. ticker symbols stay UPPERCASE letters (AAPL, never aapl) — no dollar sign, it reads aloud badly.
when real depth exists beyond the essentials, end with one short offer to break it down in chat.`

/** Chat's rule for writing tickers; a spoken turn has its spoken style in this place. */
const CASHTAG_RULE =
  'write stock ticker symbols as UPPERCASE cashtags every single time you mention one ($AAPL, $BRK.B) — never lowercase, never bare.'

/** The column keys tool calls take, in the stable head of every chat-model prompt. */
const COLUMN_KEYS = `## column keys (for tool calls only)
session_lp (price), session_chg_p (change %), session_v (volume), marketcap, sharesfloat (float), sharesoutstanding, share_group, rvol, sector, halt, pm_chg_p, ah_chg_p, dollar_vol
for others: search_knowledge(domain="column"). invalid keys are rejected.`

/**
 * What a chat-model turn's job is, by its access (the turn check decides it
 * per turn). On a read-only turn the model answers and never mutates (and has
 * no mutation tools); on a turn that may change the workspace it acts. Typed
 * and spoken turns alike.
 */
const READ_PRINCIPLES = `## principles (read-only)
1. answer, don't act. you explain, analyze, and teach — you do NOT modify the workspace, and you have no tools that change it.
2. ground every answer in data. prefer ask_analyst for market-data questions (why a ticker moved, how often, what happens after a setup, screening) get_platform_guide for platform how-to ("where is", "how do I"), and search_knowledge for what a trading term means (domain concept) and for scan/column/strategy recipes.
3. HOW-TO answers are quotes, not improv: when get_platform_guide returns a guide, your answer MUST come from it — repeat the exact control names and paths it gives (gear icon, tab names) VERBATIM. NEVER invent navigation or menu paths. when no guide covers it, still be useful: say in one plain line that you don't have a guide on that yet, then give the most helpful thing you can without inventing UI (what the feature or term means, an answer to the underlying question, or what you can do for them right now). never reply with only "not documented". the app offers the user a Request this button for the missing guide.
4. the ## open panels inventory is ground truth. never tell the user to open a panel that is already listed there, and never claim a panel is open when it isn't.
5. when your answer references a control that appears in ## interactive elements, end with [POINT:<its-label>] so the cursor shows the user exactly where it is.
6. if the user clearly wants an action ("add a scanner", "build this"), answer their question first, then say you can make the change if they ask for it.
7. be concise. lead with the direct answer.`

const CHANGE_PRINCIPLES = `## principles
1. act, don't instruct. never tell the user to click buttons — do it yourself with tools.
2. read before you write. call read_panel_state before modifying any panel.
3. "new" means create. "add a new scanner" = add_panel first, then configure it using the returned panel_id.
4. one panel, no ambiguity. if multiple panels match, use request_panel_selection. if you just created a panel, use its panel_id directly.
5. be concise. one sentence if nothing changed.`

/**
 * How long a turn waits for the turn check before it says so. Past it the turn
 * shows a status line (`showSlowStatus`) and keeps waiting.
 */
const ROUTE_CHECK_SLOW_MS = 1500

/**
 * How long a turn waits for the turn check before falling back to the host's
 * hint: 4500 ms, the longest the server keeps trying, plus a 1000 ms margin for
 * the hop.
 */
const ROUTE_CHECK_DEADLINE_MS = 5500

/** The slow-check status line (Chris, 2026-10-20), in Spanish when the AI language is. */
const SLOW_ROUTE_STATUS = 'Taking a bit more time, just a moment.'
const SLOW_ROUTE_STATUS_ES = 'Esto está tardando un poco más, un momento.'

/**
 * How long a concept turn waits for its prefetched search before round 0, from
 * the turn check's answer (the search starts with the check). Past it the turn
 * goes on without it, and the model calls the tool itself, as before.
 */
const KNOWLEDGE_PREFETCH_DEADLINE_MS = 2000

/** The browser tool that reads the chart on screen (the app's chart tools register it). */
const READ_CHART = 'read_chart'

/**
 * The bars a chart turn reads before round 0 (KnowledgePrefetchHost.chart), not
 * read_chart's default 100: the 8 live answers on 2026-10-22 used the ticker,
 * timeframe, session levels, last bar, drawings and indicators, never the bar
 * list. The result's note says to pass from/to when a question needs more.
 */
const CHART_PREFETCH_BARS = 5

/** The concept search in flight for the current turn (KnowledgePrefetchHost.concept). */
interface ConceptSearch {
  result: Promise<{ input: Record<string, unknown>; content: string } | null>
  abort: () => void
}

/** The promise's value, or null once `ms` pass first (`onLate` runs then). */
async function withDeadline<T>(
  promise: Promise<T>,
  ms: number,
  onLate?: () => void,
): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const late = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      onLate?.()
      resolve(null)
    }, ms)
  })
  try {
    return await Promise.race([promise, late])
  } finally {
    clearTimeout(timer)
  }
}

/**
 * A status line shown as its own assistant message: the analyst's acknowledgement
 * ("Checking your watchlists now.") or the slow-check line (`showSlowStatus`). It
 * is status, never context: the history sent with the next analyst or chat request
 * leaves it out. The flag lives on the message in memory only; a saved thread keeps
 * the text, not the flag.
 */
type AckMessage = Message & { ack: true }

function isAck(message: Message): message is AckMessage {
  return (message as AckMessage).ack === true
}

/**
 * The slow-check status in the AI language. Spanish only when the trader chose it:
 * a language inferred from the browser does not force a reply language (the AI
 * follows the message), so it does not force this line either.
 */
function slowRouteStatus(language: string, explicit: boolean): string {
  return explicit && language === 'Spanish' ? SLOW_ROUTE_STATUS_ES : SLOW_ROUTE_STATUS
}

/**
 * The token of the companion turn in progress, for app code that calls a
 * backend on that turn's behalf (the ask_analyst tool) so the call's analytics
 * join the turn. One companion runs per page; null between turns.
 */
let activeTurnToken: string | null = null

export function currentTurnToken(): string | null {
  return activeTurnToken
}

/** The turn in progress's `client_turn_id`, for the same app code (ask_analyst); null between turns. */
let activeClientTurnId: string | null = null

export function currentClientTurnId(): string | null {
  return activeClientTurnId
}

/**
 * Whether the turn in progress is typed or spoken, for the same app code: on a
 * spoken turn ask_analyst asks for the spoken answer, which the turn then
 * speaks as it is. Null between turns.
 */
let activeTurnModality: 'text' | 'voice' | null = null

export function currentTurnModality(): 'text' | 'voice' | null {
  return activeTurnModality
}

/** What a turn has done so far, for its `ai_turn` record (CompanionConfig.onTurnTelemetry). */
interface TurnStats {
  id: string
  startedAt: number
  modality: 'text' | 'voice'
  path: 'chat' | 'analyst'
  firstTextAt: number | null
  ackAt: number | null
  rounds: number
  toolsCalled: Record<string, number>
  getToolsLookups: number
  notOffered: string[]
  promptTokens: number | null
  cachedTokens: number | null
  errored: boolean
  /** How the turn's chat requests sent their tools (TOOL_DELIVERY). */
  toolDelivery: ToolDelivery
}

/** A running sum that stays null until a stream reports a number. */
export const addKnown = (sum: number | null, n: unknown): number | null =>
  typeof n === 'number' ? (sum ?? 0) + n : sum

/** What every AI surface says when the month's allowance is used up (a 402). */
export const ALLOWANCE_SPENT_MESSAGE = "You've used this month's AI allowance."

/** The month's AI allowance is used up (a 402): shown as it is, never as "Error: ...". */
export class AllowanceSpentError extends Error {}

export class CompanionManager {
  readonly events = new EventBus()
  readonly context = new ContextCollector()

  /**
   * Stable id for this manager instance. Used only by the dev debug bridge to
   * group every round under a single sidebar tab. Not sent to the backend.
   */
  readonly conversationId = `companion-${++companionConversationSeq}`

  /** Observer fired once per model round when the debug bridge is attached. */
  private debugRoundListener: ((round: CompanionDebugRound) => void) | null = null
  /** Per-turn counter + id for the debug view, assigned when a turn begins (beginTurn). */
  private _debugTurnSeq = 0
  private _debugTurnId = ''
  /**
   * A round that called tools defers its debug emit until the caller has
   * measured tool-execution time, so tool time is attributed to the round that
   * requested it (see `_flushPendingDebugRound` + the caller tool loops).
   */
  private _pendingDebugRound: Omit<CompanionDebugRound, 'toolMs'> | null = null

  private _state: CompanionState = 'idle'
  private _messages: Message[] = []
  private _config: CompanionConfig
  private abortController: AbortController | null = null
  /** One controller spans every model/tool round for one user turn. */
  private turnAbortController: AbortController | null = null
  private turnId = ''
  /**
   * The server's proof that this turn's credit is paid (`turn` SSE event). It
   * rides every later tool round of the same turn so those rounds are not
   * charged again, and is dropped when a new turn begins or the turn is cancelled.
   */
  private turnToken: string | null = null
  /**
   * The chat thread this conversation is (the app's thread id). It rides every
   * request as `session_id`, so analytics groups a conversation's turns
   * (PostHog $ai_session_id). Analytics only: the server never trusts it.
   */
  private _sessionId: string | null = null
  private _lastCaptureSize = { width: 1280, height: 720 }
  /**
   * Explicit page context from the host app (route-based). The DOM heuristic
   * (data-panel-type scan) only works on workspace pages — the backtest
   * editor has no workspace panels, so without this override it
   * misclassifies as 'dashboard' and never offers the backtest tools.
   */
  private _pageContext: PageContext | null = null
  /**
   * The access every turn starts from. `read` = read-only (analyze/explain);
   * `change` = may change the workspace (mutations). The app leaves it at
   * `read`: since 2026-10-16 (Build removed) the turn check decides per turn.
   * A host without a turn check (the evals) sets `change` to act on every
   * turn. Default: the safe `read`.
   */
  private _baseAccess: TurnAccess = 'read'
  /**
   * The access of the turn in progress (2026-10-15, one-assistant Phase 2b):
   * the base access or, on a host with a direct route, the turn check's
   * verdict that the turn may change the workspace. Every prompt and tool gate
   * inside a turn reads this, not `_baseAccess`.
   */
  private _turnAccess: TurnAccess = 'read'
  /** Whether the previous turn of this conversation could change the workspace. */
  private _lastTurnActed = false
  /**
   * What this thread waits on from the trader (spec 2026-10-18, AI companion
   * growth architecture, 6.2): a question or choices from the analyst, or a
   * walkthrough parked behind its Start button. One slot: a new question, New
   * chat and a thread switch clear it, and a reply to the analyst's question
   * goes back to the analyst unless it is a change request (2026-10-18: "My
   * Watchlist (2)", the answer to the analyst's "Which one?", read as small
   * talk and went to the assistant, which could not see the lists). The answer
   * that sets it carries it as `pending`, so the thread keeps it (`loadMessages`).
   */
  private _pending: Pending | null = null
  /**
   * This turn's request read as a change to the host, but the check kept it
   * read-only: the answer offers "Do it" (see `suggestChangeText`).
   */
  private _offerChange = false
  /**
   * This turn's concept search, kept when the turn check read the turn as a
   * concept question; round 0 takes it once.
   */
  private _prefetch: ConceptSearch | null = null
  /**
   * This turn reads the chart on screen before round 0: the turn check said it
   * asks about the trader's own chart (`reads_chart`). Round 0 takes it once.
   */
  private _chartPrefetch = false
  /** What the trader did to the AI's changes since its last reply (an Undo). */
  private _outcomeNotes: string[] = []
  /** The turn in progress, for its `ai_turn` record; null between turns. */
  private _turnStats: TurnStats | null = null
  /**
   * The server's TOOL_DELIVERY as the last turn check (/route) named it (CACHE
   * step 2, 2026-10-19); legacy until one does, and for a server that sends
   * none. A turn that skips the check keeps it. `CompanionConfig.toolDelivery`
   * wins over it.
   */
  private _routeDelivery: ToolDelivery = 'legacy'
  /** Language detected from the last voice transcription (e.g., "es", "fr") */
  detectedLanguage = 'en'

  /**
   * Forced response language (English NAME, e.g. "English", "Spanish"). Explicit
   * and mirrored from the app's language preference — the AI always answers in
   * this, regardless of the message or tool-result language. Not auto-detected.
   */
  private _language = 'English'
  /** False when `_language` is only the browser-inferred fallback (see `languageRule`). */
  private _languageForced = true

  /** Set the access every turn starts from (see `_baseAccess`). */
  setBaseAccess(access: TurnAccess): void {
    if (access !== this._baseAccess) this.cancel()
    this._baseAccess = access
    this._turnAccess = access
  }

  /**
   * Record what the trader did to the AI's changes (2026-10-15, one-assistant
   * Phase 2c): an Undo. The model hears it once, on its next turn, in the
   * system prompt: never in the user text, which the turn check reads.
   */
  noteActionOutcome(line: string): void {
    this._outcomeNotes.push(line)
  }

  /** The outcome notes as a prompt block, emptied once taken. */
  private takeOutcomeNotes(): string {
    if (this._outcomeNotes.length === 0) return ''
    const block = `\n\n## since your last reply\n${this._outcomeNotes.map((line) => `- ${line}`).join('\n')}`
    this._outcomeNotes = []
    return block
  }

  /**
   * Host-app persona framing (the assistant's block). Appended in the STABLE
   * prompt region of both send paths: the host sets it once. Empty = none.
   */
  private _personaFraming = ''

  setPersonaFraming(text: string): void {
    this._personaFraming = text
  }

  /**
   * The active persona id, sent with every chat request so the SERVER can
   * enforce the persona's tool surface and framing (2026-09-20). The framing
   * text and tool filter above are the client-side mirror that keeps the
   * prompt lean; this is the enforcement signal.
   */
  private _persona: CompanionPersonaId | null = null

  setPersona(persona: CompanionPersonaId | null): void {
    this._persona = persona
  }

  /**
   * Host-injected tool restriction, which also keeps the prompt lightweight
   * (the tool index, schemas, and tool-gated prompt context all shrink with
   * it). Composes with the read/change access gate; `get_tools` itself always
   * survives so on-demand loading keeps working. Null = unrestricted (the app
   * since 2026-10-15; the evals may restrict).
   */
  private _toolFilter: ((name: string) => boolean) | null = null

  setToolFilter(filter: ((name: string) => boolean) | null): void {
    this._toolFilter = filter
  }

  /** The composed filter handed to the registry (meta tools always pass). */
  private toolFilter(): ((name: string) => boolean) | undefined {
    const f = this._toolFilter
    if (!f) return undefined
    return (name) => name === 'get_tools' || f(name)
  }

  private filterAlwaysLoaded(tools: ToolDefinition[]): ToolDefinition[] {
    const f = this.toolFilter()
    return f ? tools.filter((t) => f(t.name)) : tools
  }

  /** How this turn's chat requests send their tools (CACHE step 2; tools/tool-delivery.ts). */
  private toolDelivery(): ToolDelivery {
    return this._config.toolDelivery ?? this._routeDelivery
  }

  /**
   * The tool list a round sends. `tools` is what the turn may call: the core
   * and what get_tools loaded, at the end. On fixed every round sends the same
   * list, and what get_tools loaded is called through use_tool.
   */
  private sentTools(
    delivery: ToolDelivery,
    core: readonly ToolDefinition[],
    tools: ToolDefinition[],
  ): ToolDefinition[] {
    return delivery === 'fixed' ? fixedRoundTools(core) : tools
  }

  /**
   * One round's calls as the host runs them. On fixed, a use_tool call becomes
   * the call of the tool it names when the turn offers it (`callable`: the core
   * and what get_tools loaded) and its args match that tool's schema; any other
   * is refused here with what to fix, and never reaches the host.
   */
  private roundCalls(
    delivery: ToolDelivery,
    calls: ToolCall[],
    callable: readonly ToolDefinition[],
  ): { calls: ToolCall[]; rejected: RefusedCall[] } {
    return delivery === 'fixed' ? unwrapUseTool(calls, callable) : { calls, rejected: [] }
  }

  /**
   * The results the model reads back, one per call it made, in its order. On
   * fixed, a refused use_tool call answers with what to fix, and get_tools'
   * result says to call what it loaded through use_tool. Otherwise the host's
   * results, as they came.
   */
  private answeredResults(
    delivery: ToolDelivery,
    made: ToolCall[],
    results: ToolResult[],
    rejected: RefusedCall[],
  ): ToolResult[] {
    if (delivery !== 'fixed') return results
    const byId = new Map(
      [...results, ...rejected.map((r) => r.result)].map((r) => [r.tool_use_id, r]),
    )
    return made.flatMap((call) => {
      const r = byId.get(call.tool_use_id)
      if (!r) return []
      return call.name === 'get_tools' && r.success
        ? [{ ...r, content: `${r.content}\n\n${LOADED_THROUGH_USE_TOOL}` }]
        : [r]
    })
  }

  /** The access of the turn in progress (see `_turnAccess`). */
  get turnAccess(): TurnAccess {
    return this._turnAccess
  }

  /** What this thread waits on from the trader (see `_pending`); null when nothing. */
  get pending(): Pending | null {
    return this._pending
  }

  /**
   * The trader answered or dismissed what the slot holds (Start walkthrough,
   * Dismiss). Nothing happens unless it holds that kind. The answer that
   * carried it forgets it, so a reopened thread does not offer it again.
   */
  withdraw(kind: PendingKind): void {
    const current = this._pending
    if (current?.kind !== kind) return
    const i = this._messages.length - 1
    const last = this._messages[i]
    if (last?.pending === current) {
      const kept = { ...last }
      delete kept.pending
      this._messages[i] = kept
    }
    this.setPending(null)
  }

  private setPending(next: Pending | null): void {
    if (next === this._pending) return
    this._pending = next
    this.events.emit({ type: 'pending', pending: next })
  }

  /** A new question clears the slot; the turn keeps what its message answers (prepareTurn). */
  private takePending(): Pending | null {
    const answering = this._pending
    this.setPending(null)
    return answering
  }

  /**
   * What a round's calls did to the slot, in call order. A call that settled
   * what it holds withdraws it (a walkthrough the call started, as Start
   * walkthrough does); a call that parked something for the trader (a
   * walkthrough) holds the slot from here, and the turn's answer carries it, so
   * the thread keeps it.
   */
  private updateSlot(results: readonly ToolResult[]): void {
    for (const r of results) {
      if (r.withdraws) this.withdraw(r.withdraws)
      if (r.pending?.kind === 'walkthrough_offer') this.setPending(r.pending)
    }
  }

  private languageRule(forcedRule: string): string {
    return languageRule(this._language, this._languageForced, forcedRule)
  }

  /** Set the forced response language (English name, e.g. "Spanish"). */
  setLanguage(languageName: string, forced = true): void {
    this._language = languageName
    this._languageForced = forced
  }

  constructor(config: CompanionConfig) {
    this._config = config
  }

  // ── Getters ────────────────────────────────────────────────

  get state(): CompanionState {
    return this._state
  }

  get messages(): readonly Message[] {
    return this._messages
  }

  get config(): CompanionConfig {
    return this._config
  }

  get lastCaptureSize(): { width: number; height: number } {
    return this._lastCaptureSize
  }

  // ── Config ───────��─────────────────────────────────────────

  updateConfig(partial: Partial<CompanionConfig>): void {
    this._config = { ...this._config, ...partial }
  }

  /** Set the route-derived page context (null = fall back to the DOM heuristic). */
  setPageContext(page: PageContext | null): void {
    this._pageContext = page
  }

  /**
   * Register a per-round profiling observer (dev debug sidebar). Fires once for
   * every `streamChat` call with latency + inputs/outputs. Returns an
   * unsubscribe fn. Mirrors the datasource `onDebug` pattern; the package never
   * imports the app's debug registry, so the app maps + forwards these.
   */
  onDebug(cb: (round: CompanionDebugRound) => void): () => void {
    this.debugRoundListener = cb
    return () => {
      if (this.debugRoundListener === cb) this.debugRoundListener = null
    }
  }

  /**
   * Emit a deferred tool round with its measured tool-execution time. Called by
   * the caller tool loops right after the tools resolve. No-op when nothing is
   * pending (e.g. no observer, or the round had no tool calls). Idempotent, so
   * the safety-net flush at the top of `streamChat` can never double-emit.
   */
  private _flushPendingDebugRound(toolMs: number | undefined): void {
    const draft = this._pendingDebugRound
    if (!draft) return
    this._pendingDebugRound = null
    if (!this.debugRoundListener) return
    try {
      this.debugRoundListener({ ...draft, toolMs })
    } catch (err) {
      console.warn('[companion:debug] round emit failed', err)
    }
  }

  /** Route override wins; the legacy DOM heuristic is the fallback. */
  private resolvePage(panelTypes: string[]): PageContext {
    if (this._pageContext) return this._pageContext
    if (panelTypes.some((t) => t.startsWith('history-'))) return 'history'
    if (panelTypes.some((t) => ['priceData', 'entryRules', 'trade', 'simpleTrade'].includes(t))) {
      return 'backtest'
    }
    return 'dashboard'
  }

  /** Rebind the model prompt and schemas after a navigation callback settles. */
  private refreshLoopContext(
    prompt: string,
    panelTypes: string[],
    page: PageContext,
    previousTools: ToolDefinition[],
  ): {
    prompt: string
    page: PageContext
    /** The new page's core; `tools` is it plus what was loaded that it still offers. */
    core: ToolDefinition[]
    tools: ToolDefinition[]
    indexedToolNames: string[]
  } {
    const indexedToolNames = getIndexedToolNames(
      panelTypes,
      page,
      this._turnAccess,
      this.toolFilter(),
    )
    const core = this.filterAlwaysLoaded(getToolsForContext(panelTypes, page))
    const tools = [...core]
    const names = new Set(tools.map((tool) => tool.name))
    for (const tool of previousTools) {
      if (indexedToolNames.includes(tool.name) && !names.has(tool.name)) {
        tools.push(tool)
        names.add(tool.name)
      }
    }

    // The previous round may have appended a scope-specific tool index,
    // catalog, or prompt extension. Remove those blocks before rebuilding so a
    // route change cannot leave the model with stale tools from the old page.
    let nextPrompt = prompt
      .replace(
        /\n\n## additional tools \(call get_tools to load schema before using\)\n(?:- .*\n?)+/g,
        '',
      )
      .replace(
        /\n\n## available backtest node types[\s\S]*?(?=\n\n## (?:tool principles|backtest editor|current page|current workspace|interactive elements|this turn)|$)/g,
        '',
      )
      .replace(
        /\n\n## available routes \(for navigate_to\)[\s\S]*?(?=\n\n## (?:tool principles|backtest editor|current page|current workspace|interactive elements|this turn)|$)/g,
        '',
      )
      .replace(
        /\n\n## backtest editor[\s\S]*?(?=\n\n## (?:tool principles|current page|current workspace|interactive elements|this turn)|$)/g,
        '',
      )
      .replace(
        /\n\n## current page\n[\s\S]*? — the user is already here; only navigate_to a route when they ask to go somewhere else\./,
        '',
      )
    const currentPage = `\n\n## current page\n${page} — the user is already here; only navigate_to a route when they ask to go somewhere else.`
    nextPrompt += currentPage
    const activeToolNames = [...tools.map((tool) => tool.name), ...indexedToolNames]
    const toolIndex = getToolIndex(panelTypes, page, this._turnAccess, this.toolFilter())
    if (toolIndex) nextPrompt += `\n\n${toolIndex}`
    nextPrompt = appendToolContext(nextPrompt, activeToolNames)
    const scope = getRegisteredScope(page)
    if (this._turnAccess === 'change' && scope?.promptExtras) {
      nextPrompt += `\n${scope.promptExtras}`
    }
    return { prompt: nextPrompt, page, core, tools, indexedToolNames }
  }

  // ── State transitions ──────────────────────────────────────

  private setState(state: CompanionState): void {
    this._state = state
    this.events.emit({ type: 'state-change', state })
  }

  /** Name the chat thread this conversation's requests belong to (null: none yet). */
  setSessionId(id: string | null): void {
    this._sessionId = id
  }

  private setTurnToken(token: string | null): void {
    this.turnToken = token
    activeTurnToken = token
  }

  /** Start a user turn and invalidate any previous turn still awaiting work. */
  private beginTurn(modality: 'text' | 'voice'): TurnContext {
    this._config.directDataRoute?.cancelSpeech?.()
    this.turnAbortController?.abort()
    // A turn still open here never finished: it was stopped or superseded.
    this.closeTurnStats('stopped')
    const controller = new AbortController()
    this.turnAbortController = controller
    this.setTurnToken(null)
    this._debugTurnSeq += 1
    // A fresh id per user turn (RFC 4122 v4, TEL 2026-10-19): the
    // `client_turn_id` every request of the turn sends, and the key the
    // browser's `ai_turn` and the server's events of the turn join on, so it
    // must be unique across page loads, which a per-page counter is not.
    this.turnId = crypto.randomUUID()
    this._debugTurnId = this.turnId
    activeClientTurnId = this.turnId
    activeTurnModality = modality
    this._turnStats = {
      id: this.turnId,
      startedAt: performance.now(),
      modality,
      path: 'chat',
      firstTextAt: null,
      ackAt: null,
      rounds: 0,
      toolsCalled: {},
      getToolsLookups: 0,
      notOffered: [],
      promptTokens: null,
      cachedTokens: null,
      errored: false,
      toolDelivery: this.toolDelivery(),
    }
    return { id: this.turnId, signal: controller.signal }
  }

  private isCurrentTurn(turn: TurnContext): boolean {
    return this.turnAbortController?.signal === turn.signal
  }

  /** Release the turn after a normal completion without aborting its signal. */
  private finishTurn(turn: TurnContext): void {
    if (this._turnStats?.id === turn.id) {
      this.closeTurnStats(this.stopped(turn) ? 'stopped' : undefined)
    }
    if (this.isCurrentTurn(turn)) {
      this.turnAbortController = null
      this.turnId = ''
      activeClientTurnId = null
      activeTurnModality = null
    }
  }

  /** Text reached the trader: the first delta is the turn's time to first word. */
  private noteFirstText(): void {
    if (this._turnStats && this._turnStats.firstTextAt === null) {
      this._turnStats.firstTextAt = performance.now()
    }
  }

  /** A tool the round did not offer, or a get_tools ask for one it could not load. */
  private noteNotOffered(name: string): void {
    this._turnStats?.notOffered.push(name)
  }

  /** One chat-model round went out (answered or not), and its usage when the stream reported it. */
  private noteChatRound(usage: { tokensIn?: number; cachedTokens?: number } | undefined): void {
    const stats = this._turnStats
    if (!stats) return
    stats.rounds += 1
    stats.promptTokens = addKnown(stats.promptTokens, usage?.tokensIn)
    stats.cachedTokens = addKnown(stats.cachedTokens, usage?.cachedTokens)
  }

  /**
   * A round's calls as `roundCalls` resolved them, against the tools the turn
   * may call (`callable`): the calls the host runs, and the use_tool calls
   * refused before it, each under the tool it named.
   */
  private noteRoundCalls(
    round: { calls: ToolCall[]; rejected: RefusedCall[] },
    callable: readonly ToolDefinition[],
  ): void {
    const stats = this._turnStats
    if (!stats) return
    const offered = new Set(callable.map((t) => t.name))
    for (const name of [...round.calls.map((c) => c.name), ...round.rejected.map((r) => r.name)]) {
      if (!offered.has(name)) this.noteNotOffered(name)
      else if (name === 'get_tools') stats.getToolsLookups += 1
      else stats.toolsCalled[name] = (stats.toolsCalled[name] ?? 0) + 1
    }
  }

  /**
   * Send the turn's one `ai_turn` record to the host (TEL, 2026-10-19). Ids,
   * counts, times and tool names only: nothing the trader or the AI wrote.
   */
  private closeTurnStats(outcome?: 'stopped'): void {
    const stats = this._turnStats
    if (!stats) return
    this._turnStats = null
    const report = this._config.onTurnTelemetry
    if (!report) return
    const since = (at: number | null) => (at === null ? null : Math.round(at - stats.startedAt))
    const turn: AiTurnTelemetry = {
      clientTurnId: stats.id,
      path: stats.path,
      modality: stats.modality,
      // The analyst never changes the workspace; the chat path's turn access
      // says whether it could.
      access: stats.path === 'analyst' ? 'read' : this._turnAccess,
      outcome: outcome ?? (stats.errored ? 'error' : 'answered'),
      ttfwMs: since(stats.firstTextAt),
      ackMs: since(stats.ackAt),
      totalMs: Math.round(performance.now() - stats.startedAt),
      rounds: stats.rounds,
      toolsCalled: stats.toolsCalled,
      getToolsLookups: stats.getToolsLookups,
      notOffered: stats.notOffered,
      promptTokens: stats.promptTokens,
      cachedTokens: stats.cachedTokens,
      toolDelivery: stats.toolDelivery,
    }
    try {
      report(turn)
    } catch (e) {
      console.warn('[companion] turn telemetry failed', e)
    }
  }

  /**
   * Load what a round's get_tools calls asked for, from this turn's index. A
   * name the index does not hold (a change tool on a read-only turn, or no such
   * tool) is not offered: it is dropped, and recorded.
   */
  private loadRequestedTools(
    toolCalls: ToolCall[],
    tools: ToolDefinition[],
    indexedToolNames: string[],
  ): void {
    for (const tc of toolCalls) {
      if (tc.name === 'get_tools' && tc.input.tools) {
        const requested = tc.input.tools as string[]
        const existingNames = new Set(tools.map((t: ToolDefinition) => t.name))
        for (const name of requested) {
          const def = getToolDefinition(name)
          if (!existingNames.has(name) && indexedToolNames.includes(name) && def) {
            tools.push(def)
            existingNames.add(name)
          } else if (!existingNames.has(name)) {
            this.noteNotOffered(name)
          }
        }
      }
    }
  }

  private stopped(turn: TurnContext): boolean {
    return turn.signal.aborted
  }

  // ── Main API ───��───────────────────────────────────────────

  /**
   * Send a spoken message (the voice transcript) to the companion. The turn is
   * built as a typed one (buildTurnPrompt), with the spoken style.
   * Optionally accepts a pre-captured screenshot; otherwise the screen is
   * captured only when classify says the turn needs the image.
   */
  async sendMessage(text: string, preCaptured?: CaptureResult): Promise<void> {
    if (this._state !== 'idle' && this._state !== 'listening') return
    const answering = this.takePending()

    // Add user message
    const userMessage: Message = {
      role: 'user',
      text,
      timestamp: Date.now(),
    }
    this._messages.push(userMessage)
    this.events.emit({ type: 'message', message: userMessage })

    this.setState('processing')
    const turn = this.beginTurn('voice')

    // Voice-asked market questions get the short SPOKEN answer contract from
    // the analyst (useCompanion speaks the emitted messages: ack first, then
    // the answer). The turn check decides that, and the turn's access.
    if (await this.prepareTurn(text, 'voice', turn, answering)) {
      this.finishTurn(turn)
      return
    }

    const t0 = performance.now()
    const lap = (label: string) => {
      const ms = (performance.now() - t0).toFixed(0)
      console.log(`[companion:timing] ${ms}ms — ${label}`)
    }

    // Live speech (2026-10-02): speak the reply sentence by sentence as it
    // streams. Waiting for the whole reply, then synthesizing it as one clip,
    // put ~6 s of silence before the first word.
    const speech = this.openLiveSpeech()
    let spokeLive = false
    // Cut (2026-10-22): stop speaking now, dropping what is queued, when what
    // was heard is not the answer: a reset (the server's guard withdrew the
    // finished answer to say a database key in words or take advice out, then
    // sent the whole fix), or an analyst answer that failed partway. Text
    // streams faster than speech, so much of a draft still waits then. The
    // rest of the turn is not spoken live: the clip player reads its answer.
    let speechCut = false
    const cutSpeech = () => {
      if (!speech || speechCut) return
      speechCut = true
      speech.cancel()
    }
    // The analyst's text is spoken as it streams only while a round that can
    // pass through runs its tools (`speakAnalyst`, set in the tool loop);
    // `analystText` is the part of that round's analyst text that was spoken.
    let speakAnalyst = false
    let analystText = ''
    const stopForwarding = speech
      ? this.events.on((ev) => {
          if (this.stopped(turn) || speechCut) return
          if (ev.type !== 'text-delta' && ev.type !== 'text-reset') return
          if (ev.source !== 'companion' && !(ev.source === 'analyst' && speakAnalyst)) return
          if (ev.type === 'text-reset') {
            cutSpeech()
            return
          }
          speech.onDelta(ev.text)
          spokeLive = true
          if (ev.source === 'analyst') analystText += ev.text
        })
      : null
    let speechCompleted = false

    try {
      // ── Stage 1: Classify intent (fast, no screenshot) ──────────
      const panels = collectPanelLayout()
      const panelTypes = [...new Set(panels.map((p) => p.panelType))]
      const pageForClassify = this.resolvePage(panelTypes)
      const classifyResult = await this.classifyIntent(text, panelTypes, pageForClassify)
      lap('classify done')
      if (this.stopped(turn)) return

      // ── Stage 2: Targeted response ────────────

      // Capture + annotate only when classify says the turn needs the image:
      // the capture clones the whole DOM on the main thread and can freeze a
      // large dashboard for seconds. Text-only turns still need the coordinate
      // space for the panel layout, so measure it without capturing.
      let annotatedBase64: string | undefined
      if (classifyResult.needs_image) {
        const capture = preCaptured ?? (await captureScreen(this._config.captureTarget))
        lap('screenshot done')
        if (this.stopped(turn)) return
        this._lastCaptureSize = { width: capture.width, height: capture.height }
        annotatedBase64 = await annotateScreenshot(
          capture.base64,
          panels,
          capture.width,
          capture.height,
        )
        if (this.stopped(turn)) return
        lap('annotate done')
      } else {
        this._lastCaptureSize = preCaptured
          ? { width: preCaptured.width, height: preCaptured.height }
          : measureCapture(this._config.captureTarget)
      }

      // Build panel layout — pass classify target so only that panel gets full detail
      const panelLayoutDesc = describePanelLayout(
        panels,
        this._lastCaptureSize.width,
        this._lastCaptureSize.height,
        mousePosition.x,
        mousePosition.y,
        classifyResult.panel,
      )

      // ── Stage 2b: Resolve tools ──
      let page = this.resolvePage(panelTypes)

      // Always attach the always-loaded tools + the access-gated tool index:
      // ACCESS, not the classifier, decides what can mutate. The always-loaded set
      // is entirely read-only (get_platform_guide, ask_analyst, search_knowledge,
      // read_panel_state, …); mutation tools only ever reach the model through the
      // index, which drops them on a read-only turn. Gating attachment on
      // classifyResult.needs_actions used to leave read-only voice with NO tools at
      // all — not even get_platform_guide (the how-to / walkthrough trigger). The
      // chat path already always-attaches; this makes the voice path match.
      // Initialized to the always-loaded set (never undefined); the tool-loop
      // below push()es on-demand schemas onto it as get_tools loads them.
      // TOOL_DELIVERY decides the list each round sends (`sentTools`): `tools`
      // is what the turn may call.
      const delivery = this.toolDelivery()
      let core: ToolDefinition[] = this.filterAlwaysLoaded(getToolsForContext(panelTypes, page))
      let indexedToolNames = getIndexedToolNames(
        panelTypes,
        page,
        this._turnAccess,
        this.toolFilter(),
      )
      let tools: ToolDefinition[] = [...core]
      if (tools.length > 0) {
        lap(`tools resolved (${tools.length} tools for ${page})`)
      }
      const activeToolNames = [...tools.map((t: ToolDefinition) => t.name), ...indexedToolNames]

      // The prompt is chat's (buildTurnPrompt) with the spoken style; what
      // this turn sees of the screen follows the panel inventory.
      const screen: string[] = []
      if (annotatedBase64) {
        screen.push(
          `## screenshot\nthe screenshot attached to this message has dimensions: ${this._lastCaptureSize.width}x${this._lastCaptureSize.height} pixels. use these dimensions as the coordinate space for pointing, only when a screenshot is provided. prefer [POINT:<element-label>] for any element listed in ## interactive elements; for a spot on the screenshot with no label, use [POINT:x,y:label] with x,y in those pixels.`,
        )
      }
      if (panelLayoutDesc) screen.push(panelLayoutDesc)
      const built = this.buildTurnPrompt({
        modality: 'voice',
        text,
        page,
        panels,
        panelTypes,
        activeToolNames,
        classify: classifyResult,
        screen,
      })
      let fullSystemPrompt = built.systemPrompt
      const stableHead = built.stableHead

      lap(`prompt built (${fullSystemPrompt.length} chars)`)

      console.log('[companion] classify:', classifyResult)
      console.log('[companion] needs_image:', classifyResult.needs_image)
      console.log('[companion] needs_actions:', classifyResult.needs_actions)
      if (tools.length)
        console.log(
          '[companion] tools:',
          tools.map((t: ToolDefinition) => t.name),
        )

      // Build conversation history
      const maxTurns = this._config.maxHistoryTurns ?? DEFAULT_MAX_HISTORY
      const historyMessages = this.getHistoryForApi(maxTurns)

      // ── Multi-turn tool loop (shared with chat) ──────────
      let currentMessages = [...historyMessages]
      const MAX_TOOL_ROUNDS = 8
      let finalText = ''
      let finalToolCalls: ToolCall[] = []
      let finalToolResults: ToolResult[] = []
      let finalScreenshot = annotatedBase64
      let screenRequested: string | undefined
      let hitToolRoundLimit = true
      // The last round's text, calls and results: the closing text of a capped turn reads them.
      let lastRound = { text: '', calls: [] as ToolCall[], results: [] as ToolResult[] }
      // True when the final text is what streamed into live speech (the chat
      // model's, or the analyst's on a passthrough); a final that streamed
      // nothing (limit, a walkthrough intro, a fixed failure) falls back to
      // clip TTS, as does any final after a cut.
      let finalWasStreamed = false

      // A concept turn's search ran alongside the turn check, as on chat.
      const prefetched = await this.takePrefetch()
      if (this.stopped(turn)) return
      if (prefetched) {
        currentMessages = [...currentMessages, ...prefetched.exchange]
        finalToolCalls.push(prefetched.call)
        finalToolResults.push(prefetched.result)
      }
      // A question about the trader's own chart reads it here, as on chat.
      const chartRead = this.takeChartPrefetch(delivery, tools, indexedToolNames)
      if (chartRead) {
        currentMessages = [...currentMessages, ...chartRead.exchange]
        finalToolCalls.push(chartRead.call)
        finalToolResults.push(chartRead.result)
      }

      for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
        if (this.stopped(turn)) return
        const roundPage = this.resolvePage(panelTypes)
        if (roundPage !== page) {
          const refreshed = this.refreshLoopContext(fullSystemPrompt, panelTypes, roundPage, tools)
          page = refreshed.page
          core = refreshed.core
          tools = refreshed.tools
          indexedToolNames = refreshed.indexedToolNames
          fullSystemPrompt = refreshed.prompt
        }
        const streamResult = await this.streamChat({
          systemPrompt: fullSystemPrompt,
          stableHead,
          messages: currentMessages,
          // Round 0 carries an up-front capture (explicit "my screen"); later
          // rounds carry one the model asked for with view_screen.
          screenshot: round === 0 ? finalScreenshot : screenRequested,
          tools: this.sentTools(delivery, core, tools),
          pageContext: page,
          debug: { round, feature: 'companion-voice' },
        })
        if (this.stopped(turn)) return
        lap(`stream round ${round + 1} done (${streamResult.toolCalls.length} tool calls)`)

        if (streamResult.toolCalls.length === 0) {
          finalText = streamResult.text
          hitToolRoundLimit = false
          finalWasStreamed = true
          break
        }

        if (streamResult.text) finalText = streamResult.text

        // On fixed, a use_tool call is the call of the tool it names (or refused).
        const calls = this.roundCalls(delivery, streamResult.toolCalls, tools)
        this.noteRoundCalls(calls, tools)
        const toolStart = performance.now()
        // The analyst's answer is spoken as it streams in a round that can pass
        // through as called: every call an analyst one (ask_analyst,
        // summarize_history), none refused, no research feeding a change.
        speakAnalyst =
          calls.calls.length > 0 &&
          calls.calls.every((call) => TERMINAL_TOOLS.has(call.name)) &&
          this.mayPassThrough(text, calls)
        analystText = ''
        const executed =
          calls.calls.length > 0
            ? await this.runRoundTools(calls.calls, turn, panels)
            : { results: [] as ToolResult[], screenshot: undefined }
        speakAnalyst = false
        const toolResults = executed.results
        screenRequested = executed.screenshot
        if (this.stopped(turn)) return
        // Attribute this tool-execution time to the round that requested it.
        this._flushPendingDebugRound(performance.now() - toolStart)
        lap(
          `tools executed (${toolResults.filter((r: ToolResult) => r.success).length}/${toolResults.length} succeeded)`,
        )

        finalToolCalls.push(...calls.calls)
        finalToolResults.push(...toolResults)
        lastRound = { text: streamResult.text, calls: calls.calls, results: toolResults }
        this.updateSlot(toolResults)

        if (
          this._turnAccess === 'change' &&
          isResearchAndActRequest(text) &&
          calls.calls.some((call) => call.name === 'ask_analyst') &&
          toolResults.some((result) => !result.success)
        ) {
          finalText = RESEARCH_FAILURE_MESSAGE
          hitToolRoundLimit = false
          break
        }

        // Terminal passthrough, as in chat (passthroughAnswer): the analyst's
        // answer, asked for as speech on a voice turn (currentTurnModality), or
        // a launched walkthrough's authored intro IS the spoken answer, with no
        // follow-up round that would only condense it. The analyst's was heard
        // as it streamed; one that streamed nothing is left to the clip player.
        const passthrough = this.passthroughAnswer(text, calls, toolResults)
        if (passthrough !== null) {
          finalText = passthrough
          // Marked spoken only when all of it was spoken. What streamed may be
          // only its start (a later result streamed nothing, such as a fixed
          // failure): the rest is spoken now. What streamed may not be the
          // answer at all (the app stops relaying at an analyst reset): cut.
          if (analystText && !speechCut) {
            if (passthrough.startsWith(analystText)) {
              const rest = passthrough.slice(analystText.length)
              if (rest) speech?.onDelta(rest)
              finalWasStreamed = true
            } else cutSpeech()
          }
          hitToolRoundLimit = false
          break
        }
        // Some analyst text was heard, but it is not the answer (a call
        // failed): cut it there, so no fragment is followed by a different
        // answer; the clip player reads the turn's answer.
        if (analystText) cutSpeech()

        // If get_tools was called, add the requested tools for the next round
        this.loadRequestedTools(calls.calls, tools, indexedToolNames)

        // Build follow-up messages: the calls as the model made them, each answered.
        const answered = this.answeredResults(
          delivery,
          streamResult.toolCalls,
          toolResults,
          calls.rejected,
        )
        const assistantContent: ApiMessage = {
          role: 'assistant',
          content: [
            ...(streamResult.text ? [{ type: 'text' as const, text: streamResult.text }] : []),
            ...streamResult.toolCalls.map((tc: ToolCall) => ({
              type: 'tool_use' as const,
              id: tc.tool_use_id,
              name: tc.name,
              input: tc.input,
            })),
          ],
        }
        const toolResultContent: ApiMessage = {
          role: 'user',
          content: answered.map((tr: ToolResult) => ({
            type: 'tool_result' as const,
            tool_use_id: tr.tool_use_id,
            content: tr.content,
            is_error: tr.is_error,
          })),
        }
        currentMessages = [...currentMessages, assistantContent, toolResultContent]

        if (round === MAX_TOOL_ROUNDS - 1) {
          finalText = streamResult.text || ''
        }
      }

      if (hitToolRoundLimit)
        finalText = cappedTurnText(lastRound, { calls: finalToolCalls, results: finalToolResults })

      // Parse point tag
      const { point, cleanText } = parsePointTag(finalText)

      // If no text was captured, use a minimal indicator
      if (!cleanText && finalToolCalls.length > 0) {
        finalText = INCOMPLETE_RESPONSE_MESSAGE
      }

      // Finish the live speech; the message is marked spoken so the clip
      // player doesn't read it a second time.
      const spokenLive = !!speech && !speechCut && spokeLive && finalWasStreamed && !!cleanText
      if (speech) {
        if (!speechCut) speech.onDone()
        speechCompleted = true
        if (speech.finished) void speech.finished.then(() => speech.close())
        else speech.close()
      }

      // The "Do it" nudge, as on chat: the request read as a change, but the
      // check kept the turn read-only.
      const suggestChangeText = this._turnAccess === 'read' && this._offerChange ? text : undefined
      // What a tool parked this turn (a walkthrough) rides the answer (spec 6.2).
      const pending = this._pending
      const assistantMessage: Message = {
        role: 'assistant',
        text: cleanText || finalText,
        timestamp: Date.now(),
        point,
        toolCalls: finalToolCalls.length > 0 ? finalToolCalls : undefined,
        toolResults: finalToolResults.length > 0 ? finalToolResults : undefined,
        ...(suggestChangeText ? { suggestChangeText } : {}),
        ...(spokenLive ? { spoken: true } : {}),
        ...(pending ? { pending } : {}),
      }
      this._messages.push(assistantMessage)
      this.events.emit({ type: 'message', message: assistantMessage })

      if (point) {
        this.events.emit({ type: 'point', data: point })
      }

      // Stay in 'processing' state — useCompanion will transition to
      // 'responding' when TTS playback actually starts
      this.finishTurn(turn)
      return
    } catch (err) {
      if (this.stopped(turn) || isAbortError(err)) {
        if (this.isCurrentTurn(turn)) this.setState('idle')
        this.finishTurn(turn)
        return
      }
      const error = err instanceof Error ? err : new Error(String(err))
      if (this.isCurrentTurn(turn)) {
        this._emitError(error)
        this.setState('idle')
      }
      this.finishTurn(turn)
    } finally {
      stopForwarding?.()
      // Stopped, superseded (barge-in) or failed before the reply finished:
      // silence whatever was queued.
      if (speech && !speechCompleted) {
        speech.cancel()
        speech.close()
      }
    }
  }

  /**
   * Run one round's tool calls. `view_screen` is executed here (capture +
   * annotate, attached to the next round); everything else goes to the host
   * executor as before. Results come back in call order.
   */
  private async runRoundTools(
    toolCalls: ToolCall[],
    turn: TurnContext,
    panels: ReturnType<typeof collectPanelLayout>,
  ): Promise<{ results: ToolResult[]; screenshot?: string }> {
    const screenCalls = toolCalls.filter((c) => c.name === 'view_screen')
    const others = toolCalls.filter((c) => c.name !== 'view_screen')
    let screenshot: string | undefined
    const screenResults = new Map<string, ToolResult>()
    if (screenCalls.length > 0) {
      let content: string
      let success = true
      try {
        const capture = await captureScreen(this._config.captureTarget)
        this._lastCaptureSize = { width: capture.width, height: capture.height }
        screenshot = await annotateScreenshot(capture.base64, panels, capture.width, capture.height)
        content = `Screenshot attached to the next message (${capture.width}x${capture.height} px; use these dimensions as the coordinate space for pointing, and prefer [POINT:<element-label>] for labeled elements).`
      } catch (e) {
        success = false
        content = `Could not capture the screen (${e instanceof Error ? e.message : String(e)}). Answer from the data you have.`
      }
      for (const c of screenCalls) {
        screenResults.set(c.tool_use_id, {
          tool_use_id: c.tool_use_id,
          content,
          success,
          ...(success ? {} : { is_error: true }),
        } as ToolResult)
      }
    }
    const otherResults = others.length > 0 ? await this.waitForToolResults(others, turn) : []
    const byId = new Map(otherResults.map((r) => [r.tool_use_id, r]))
    const results = toolCalls.map(
      (c) => screenResults.get(c.tool_use_id) ?? byId.get(c.tool_use_id)!,
    )
    return { results: results.filter(Boolean), screenshot }
  }

  /** A live speech session for this voice reply, or null (clip TTS fallback). */
  private openLiveSpeech(): LiveSpeech | null {
    if (this._config.tts === false || !this._config.liveSpeech) return null
    try {
      return this._config.liveSpeech(this.detectedLanguage ?? null)
    } catch (e) {
      console.warn('[companion] live speech unavailable, falling back to clip TTS', e)
      return null
    }
  }

  private waitForToolResults(toolCalls: ToolCall[], turn: TurnContext): Promise<ToolResult[]> {
    return new Promise((resolve) => {
      let settled = false
      const finish = (results: ToolResult[]) => {
        if (settled) return
        settled = true
        turn.signal.removeEventListener('abort', onAbort)
        resolve(results)
      }
      const onAbort = () =>
        finish(
          toolCalls.map((call) => ({
            tool_use_id: call.tool_use_id,
            success: false,
            content: 'Task stopped.',
            is_error: true,
          })),
        )

      if (turn.signal.aborted) {
        onAbort()
        return
      }
      turn.signal.addEventListener('abort', onAbort, { once: true })
      this.events.emit({
        type: 'tool-request',
        toolCalls,
        resolve: finish,
        turnId: turn.id,
        signal: turn.signal,
      })
    })
  }

  /**
   * Send a message from the chat drawer.
   * Skips classify and screenshot. Always includes tools and panel layout.
   * Injects a conversational co-worker persona that confirms before acting.
   */
  /**
   * The turns before the current one (already pushed by the caller), for the
   * direct analyst route's follow-up context. Errors and acknowledgement lines
   * are dropped; the route trims count and size. The acknowledgement lines go
   * first: the slow-check status sits after the trader's message, and the one
   * dropped as the current turn has to stay that message.
   */
  private directRouteHistory(): Array<{ role: 'user' | 'assistant'; text: string }> {
    return this._messages
      .filter((m) => !isAck(m))
      .slice(0, -1)
      .filter(
        (m) =>
          (m.role === 'user' || m.role === 'assistant') &&
          typeof m.text === 'string' &&
          m.text.trim() !== '' &&
          !m.text.startsWith('Error:'),
      )
      .map((m) => ({ role: m.role as 'user' | 'assistant', text: m.text }))
  }

  /**
   * The host's regex verdict for a fresh turn: `analyst` for a market match,
   * `change` for an action request, else `chat`. `direct` marks an EdgeScript
   * question, streamed from the analyst at once. Null without a host route:
   * the turn is never checked and runs at the base access.
   */
  private routeHintFor(
    text: string,
  ): { route: 'analyst' | 'chat' | 'change'; ticker?: string; direct: boolean } | null {
    const route = this._config.directDataRoute
    if (!route) return null
    const match = route.detect(text)
    if (match) return { route: 'analyst', ticker: match.ticker, direct: !!match.direct }
    return { route: route.changeHint?.(text) ? 'change' : 'chat', direct: false }
  }

  /**
   * Whether this turn can read a chart: it offers read_chart (the app's chart
   * tools are registered and a chart is on screen). read_chart only reads, so
   * the read-only index decides it, before the check sets the turn's access.
   */
  private chartOnScreen(): boolean {
    const panelTypes = [...new Set(collectPanelLayout().map((p) => p.panelType))]
    const page = this.resolvePage(panelTypes)
    return getIndexedToolNames(panelTypes, page, 'read', this.toolFilter()).includes(READ_CHART)
  }

  /**
   * The turn check (2026-10-15, one-assistant Phase 2b): POST {apiBase}/route,
   * where Jev decides whether the turn goes to the analyst and whether it may
   * change the workspace. It must answer before the prompt is built, because
   * the prompt depends on both. A check slower than ROUTE_CHECK_SLOW_MS says so
   * in a status line and is waited for; a failed one, or one still silent at
   * ROUTE_CHECK_DEADLINE_MS, falls back to the hint here, so an outage never
   * blocks a turn. Its turn type decides what the turn looks up before round 0;
   * the fallback has none.
   */
  private async checkTurn(
    text: string,
    hint: { route: 'analyst' | 'chat' | 'change' },
    turn: TurnContext,
    answering: Pending | null,
  ): Promise<{
    path: 'analyst' | 'chat'
    act: boolean
    turnType: string | null
    /** The server's turn reading, for this turn's analyst request (phase 6). */
    reading: TurnReadingWire | null
    /** The turn asks about the trader's own chart: read it before round 0. */
    readsChart: boolean
  }> {
    const fallback = {
      path: hint.route === 'analyst' ? ('analyst' as const) : ('chat' as const),
      act: hint.route === 'change',
      turnType: null,
      reading: null,
      readsChart: false,
    }
    const controller = new AbortController()
    const removeTurnAbort = relayAbortSignal(turn.signal, controller)
    const timer = setTimeout(() => controller.abort(), ROUTE_CHECK_DEADLINE_MS)
    // A slow check says so once, and the turn keeps waiting for it.
    const slowTimer = setTimeout(() => this.showSlowStatus(turn), ROUTE_CHECK_SLOW_MS)
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (this._config.getAuthToken) {
        headers['Authorization'] = `Bearer ${await this._config.getAuthToken()}`
      }
      const response = await fetch(`${this._config.apiBase}/route`, {
        method: 'POST',
        headers,
        signal: controller.signal,
        body: JSON.stringify({
          message: text,
          route_hint: hint.route,
          last_turn_acted: this._lastTurnActed,
          // What the message answers: the thread's slot, when it has one.
          ...(answering ? { pending: pendingWire(answering) } : {}),
          // The turn and the thread, so the decision joins the turn's events (analytics only).
          client_turn_id: turn.id,
          ...(this._sessionId ? { session_id: this._sessionId } : {}),
          // A chart this turn can read, so a question about it can stay here (reads_chart).
          ...(this.chartOnScreen() ? { chart_on_screen: true } : {}),
        }),
      })
      if (!response.ok) return fallback
      const body = (await response.json()) as {
        path?: unknown
        act?: unknown
        turn_type?: unknown
        reading?: unknown
        tool_delivery?: unknown
        reads_chart?: unknown
      }
      // TOOL_DELIVERY (CACHE step 2): how this turn's chat requests, and later
      // ones that skip the check, send their tools. A server that sends none is legacy.
      this._routeDelivery = parseToolDelivery(body.tool_delivery) ?? 'legacy'
      if (this._turnStats?.id === turn.id) this._turnStats.toolDelivery = this.toolDelivery()
      return {
        path: body.path === 'analyst' ? 'analyst' : 'chat',
        act: body.act === true,
        turnType: typeof body.turn_type === 'string' ? body.turn_type : null,
        // Passed on untouched; the analyst validates it.
        reading:
          body.reading && typeof body.reading === 'object'
            ? (body.reading as TurnReadingWire)
            : null,
        readsChart: body.reads_chart === true,
      }
    } catch {
      return fallback
    } finally {
      clearTimeout(timer)
      clearTimeout(slowTimer)
      removeTurnAbort()
    }
  }

  /**
   * The turn check is slow: say so as its own assistant message, once, and keep
   * waiting for it (Chris, 2026-10-20: a visible wait beats a guess). Text only,
   * whatever the modality: speaking it would need a speech session open before
   * the check. An acknowledgement line: on screen, left out of every history.
   */
  private showSlowStatus(turn: TurnContext): void {
    if (this.stopped(turn)) return
    const status: AckMessage = {
      role: 'assistant',
      text: slowRouteStatus(this._language, this._languageForced),
      timestamp: Date.now(),
      source: 'chat',
      ack: true,
    }
    this._messages.push(status)
    this.events.emit({ type: 'message', message: status })
  }

  /**
   * Start the host's concept search for this message, tied to the turn: a
   * stopped or superseded turn aborts it. Null when the host has none.
   */
  private startConceptSearch(text: string, turn: TurnContext): ConceptSearch | null {
    const host = this._config.knowledgePrefetch
    if (!host) return null
    const controller = new AbortController()
    const removeTurnAbort = relayAbortSignal(turn.signal, controller)
    const result = (async () => {
      try {
        return await host.concept(text, controller.signal)
      } catch {
        return null
      } finally {
        removeTurnAbort()
      }
    })()
    return { result, abort: () => controller.abort() }
  }

  /**
   * This turn's concept search as a call and its result, for round 0 to read
   * as its own. Null when the turn searches nothing, or the search failed,
   * found nothing or ran past KNOWLEDGE_PREFETCH_DEADLINE_MS. Taken once.
   */
  private async takePrefetch(): Promise<{
    call: ToolCall
    result: ToolResult
    /**
     * The call and its result as round 0 reads them, after the trader's
     * message: the shape the round after that call had before, so it can
     * answer at once. The tools stay offered.
     */
    exchange: ApiMessage[]
  } | null> {
    const search = this._prefetch
    this._prefetch = null
    if (!search) return null
    const found = await withDeadline(search.result, KNOWLEDGE_PREFETCH_DEADLINE_MS, search.abort)
    if (!found) return null
    const id = `call_prefetch_${this._debugTurnSeq}`
    const call: ToolCall = { tool_use_id: id, name: 'search_knowledge', input: found.input }
    const result: ToolResult = { tool_use_id: id, success: true, content: found.content }
    const exchange: ApiMessage[] = [
      {
        role: 'assistant',
        content: [{ type: 'tool_use', id: call.tool_use_id, name: call.name, input: call.input }],
      },
      {
        role: 'user',
        content: [
          {
            type: 'tool_result',
            tool_use_id: result.tool_use_id,
            content: result.content,
            is_error: result.is_error,
          },
        ],
      },
    ]
    return { call, result, exchange }
  }

  /**
   * The chart on screen, read before round 0 for a turn the check says asks
   * about it (`_chartPrefetch`): the host runs read_chart's own executor, and
   * round 0 reads the call and its result as its own, so it can answer at once.
   * On fixed the call is the one the model makes, use_tool naming read_chart;
   * either way read_chart joins the callable tools, as after get_tools, so a
   * second read (a from/to window) needs no lookup. The prompt and the list
   * fixed sends stay as they were. Null when the turn does not offer read_chart
   * or the host read nothing: the turn runs as before. Taken once.
   */
  private takeChartPrefetch(
    delivery: ToolDelivery,
    tools: ToolDefinition[],
    indexedToolNames: string[],
  ): { call: ToolCall; result: ToolResult; exchange: ApiMessage[] } | null {
    const wanted = this._chartPrefetch
    this._chartPrefetch = false
    const host = this._config.knowledgePrefetch
    const def = getToolDefinition(READ_CHART)
    if (!wanted || !host?.chart || !def || !indexedToolNames.includes(READ_CHART)) return null
    let read: { input: Record<string, unknown>; content: string } | null
    try {
      read = host.chart({ max_bars: CHART_PREFETCH_BARS })
    } catch (e) {
      // The model calls read_chart itself, as before; the failure stays visible.
      console.warn('[companion] chart read before round 0 failed:', e)
      return null
    }
    if (!read) return null
    if (!tools.some((t) => t.name === READ_CHART)) tools.push(def)
    const id = `call_chart_prefetch_${this._debugTurnSeq}`
    const call: ToolCall = { tool_use_id: id, name: READ_CHART, input: read.input }
    const result: ToolResult = { tool_use_id: id, success: true, content: read.content }
    const made =
      delivery === 'fixed'
        ? { name: USE_TOOL, input: { name: READ_CHART, args: read.input } }
        : { name: READ_CHART, input: read.input }
    const exchange: ApiMessage[] = [
      { role: 'assistant', content: [{ type: 'tool_use', id, ...made }] },
      {
        role: 'user',
        content: [{ type: 'tool_result', tool_use_id: id, content: result.content }],
      },
    ]
    return { call, result, exchange }
  }

  /**
   * Decide the turn before its prompt is built. Returns true when the turn was
   * answered here (EdgeScript, or a market question sent to the analyst).
   * Otherwise sets the turn's access: the base access, or a verdict that may act.
   * `opts.act` is the "Do it" resend: the user asked for the change
   * explicitly, so there is no second check. `answering` is the slot the
   * message answers (spec 6.2): a reply to the analyst's question goes back to
   * the analyst, unchecked, unless it is a change request.
   */
  private async prepareTurn(
    text: string,
    modality: 'text' | 'voice',
    turn: TurnContext,
    answering: Pending | null,
    opts?: { act?: boolean },
  ): Promise<boolean> {
    const hint = this.routeHintFor(text)
    let act = opts?.act === true
    this._offerChange = false
    this._prefetch = null
    this._chartPrefetch = false
    if (hint && !act && hint.route !== 'change' && askedBy(answering) === 'analyst') {
      this._lastTurnActed = false
      return this.streamDirect(text, modality, turn, { ticker: hint.ticker }, answering)
    }
    if (!act && hint?.direct) {
      this._lastTurnActed = false
      return this.streamDirect(text, modality, turn, hint, answering)
    }
    if (!act && hint) {
      // A message the host reads as chat may be a concept question, typed or
      // spoken (Chris, 2026-10-22: learning questions take the same answer path
      // whatever the speed cost). The concept search has no side effects, so it
      // starts now, alongside the check; the verdict decides whether the turn
      // keeps it.
      const search = hint.route === 'chat' ? this.startConceptSearch(text, turn) : null
      const verdict = await this.checkTurn(text, hint, turn, answering)
      if (this.stopped(turn)) return true
      if (verdict.path === 'analyst') {
        search?.abort()
        this._lastTurnActed = false
        return this.streamDirect(
          text,
          modality,
          turn,
          { ticker: hint.ticker, reading: verdict.reading },
          answering,
        )
      }
      act = verdict.act
      this._offerChange = hint.route === 'change' && !act
      // The search follows the check, not the host's guess. A how-to question
      // keeps today's path (Chris, 2026-10-19): the model asks the platform
      // guide itself, and its own reading decides whether a walkthrough starts.
      const conceptTurn =
        !act && this._baseAccess !== 'change' && verdict.turnType === 'concept_definition'
      if (conceptTurn) this._prefetch = search ?? this.startConceptSearch(text, turn)
      else search?.abort()
      // A question about the trader's own chart (the check's reads_chart) reads
      // it before round 0. Never on a concept turn, which keeps its own path.
      this._chartPrefetch =
        !act && this._baseAccess !== 'change' && verdict.readsChart && !conceptTurn
    }
    this._turnAccess = this._baseAccess === 'change' || act ? 'change' : 'read'
    this._lastTurnActed = this._turnAccess === 'change'
    return false
  }

  /** Stream a market question straight from the analyst (see prepareTurn). */
  private async streamDirect(
    text: string,
    modality: 'text' | 'voice',
    turn: TurnContext,
    // `reading`: only a turn the route check read has one (phase 6).
    match: { ticker?: string; reading?: TurnReadingWire | null },
    answering: Pending | null,
  ): Promise<boolean> {
    const route = this._config.directDataRoute
    if (!route) return false
    if (this._turnStats?.id === turn.id) this._turnStats.path = 'analyst'
    try {
      const result = await route.stream({
        question: text,
        ticker: match.ticker,
        modality,
        signal: turn.signal,
        history: this.directRouteHistory(),
        pending: answering ? pendingWire(answering) : null,
        ...(match.reading ? { reading: match.reading } : {}),
        clientTurnId: turn.id,
        onAck: (ack) => {
          if (this.stopped(turn)) return
          if (this._turnStats?.id === turn.id) this._turnStats.ackAt ??= performance.now()
          const ackMessage: AckMessage = {
            role: 'assistant',
            text: ack,
            timestamp: Date.now(),
            source: 'chat',
            // Voice routes speak the ack live inside the stream session.
            spoken: modality === 'voice',
            ack: true,
          }
          this._messages.push(ackMessage)
          this.events.emit({ type: 'message', message: ackMessage })
        },
        onDelta: (delta) => {
          if (!this.stopped(turn)) {
            this.noteFirstText()
            this.events.emit({ type: 'text-delta', text: delta, source: 'analyst' })
          }
        },
        onReset: () => {
          if (!this.stopped(turn)) this.events.emit({ type: 'text-reset', source: 'analyst' })
        },
      })
      if (this.stopped(turn)) return true
      const stats = this._turnStats?.id === turn.id ? this._turnStats : null
      if (stats && result.usage) {
        stats.rounds = result.usage.rounds
        stats.promptTokens = result.usage.promptTokens
        stats.cachedTokens = result.usage.cachedTokens
      }
      // Its question or choices hold the slot: the next reply comes back here.
      const pending = analystPending(result)
      const assistantMessage: Message = {
        role: 'assistant',
        text: result.text,
        timestamp: Date.now(),
        source: 'chat',
        spoken: result.spokenLive ?? false,
        ...(result.choices?.length ? { choices: result.choices } : {}),
        ...(pending ? { pending } : {}),
      }
      this._messages.push(assistantMessage)
      this.setPending(pending)
      this.events.emit({ type: 'message', message: assistantMessage })
      if (this.isCurrentTurn(turn)) this.setState('idle')
    } catch (err) {
      if (!this.stopped(turn)) {
        console.error('[companion] direct analyst dispatch error:', err)
        if (this.isCurrentTurn(turn)) {
          this._emitError(err instanceof Error ? err : new Error(String(err)))
          this.setState('idle')
        }
      }
    }
    return true
  }

  async sendChatMessage(text: string, opts?: { act?: boolean }): Promise<void> {
    if (this._state !== 'idle' && this._state !== 'listening') {
      console.warn(`[chat] sendChatMessage blocked — state is "${this._state}", resetting to idle`)
      this.setState('idle')
    }

    const answering = this.takePending()
    const userMessage: Message = { role: 'user', text, timestamp: Date.now(), source: 'chat' }
    this._messages.push(userMessage)
    this.events.emit({ type: 'message', message: userMessage })

    this.setState('processing')
    const turn = this.beginTurn('text')

    // The turn check: the analyst for a market question (EdgeScript at once),
    // else the chat model, read-only or able to change the workspace.
    if (await this.prepareTurn(text, 'text', turn, answering, opts)) {
      this.finishTurn(turn)
      return
    }

    const t0 = performance.now()
    const lap = (label: string) => {
      const ms = (performance.now() - t0).toFixed(0)
      console.log(`[chat:timing] ${ms}ms — ${label}`)
    }

    try {
      // ── Collect panel layout (no screenshot) ──
      const panels = collectPanelLayout()
      const panelTypes = [...new Set(panels.map((p) => p.panelType))]

      // ── Classify intent (fast, parallel-safe) ──
      let page = this.resolvePage(panelTypes)
      const classifyResult = await this.classifyIntent(text, panelTypes, page)
      if (this.stopped(turn)) return
      lap('classify done')

      // ── Resolve tools ──
      // TOOL_DELIVERY decides the list each round sends (`sentTools`): `tools`
      // is what the turn may call.
      const delivery = this.toolDelivery()
      let core = this.filterAlwaysLoaded(getToolsForContext(panelTypes, page))
      let indexedToolNames = getIndexedToolNames(
        panelTypes,
        page,
        this._turnAccess,
        this.toolFilter(),
      )
      let tools = [...core]
      lap(`tools resolved (${tools.length} tools for ${page})`)

      // The prompt: the shared builder's, with chat's cashtag rule (buildTurnPrompt).
      const activeToolNames = [...tools.map((t: ToolDefinition) => t.name), ...indexedToolNames]
      const built = this.buildTurnPrompt({
        modality: 'text',
        text,
        page,
        panels,
        panelTypes,
        activeToolNames,
        classify: classifyResult,
      })
      let systemPrompt = built.systemPrompt
      const stableHead = built.stableHead

      lap(`prompt built (${systemPrompt.length} chars)`)
      console.log(
        '[chat] page:',
        page,
        'tools:',
        tools.map((t: ToolDefinition) => t.name),
      )

      // ── Stream response ──
      const maxTurns = this._config.maxHistoryTurns ?? DEFAULT_MAX_HISTORY
      const historyMessages = this.getHistoryForApi(maxTurns)

      // ── Multi-turn tool loop ──
      // Keep calling Claude until it responds with text (no more tool calls)
      let currentMessages = [...historyMessages]
      const MAX_TOOL_ROUNDS = 8
      let finalText = ''
      let finalToolCalls: ToolCall[] = []
      let finalToolResults: ToolResult[] = []
      let screenRequested: string | undefined
      let hitToolRoundLimit = true
      // The last round's text, calls and results: the closing text of a capped turn reads them.
      let lastRound = { text: '', calls: [] as ToolCall[], results: [] as ToolResult[] }

      // A concept turn's search ran alongside the turn check (prepareTurn):
      // round 0 reads it as its own call, the shape the round after that call
      // had before, and can answer at once. The tools stay offered.
      const prefetched = await this.takePrefetch()
      if (this.stopped(turn)) return
      if (prefetched) {
        currentMessages = [...currentMessages, ...prefetched.exchange]
        finalToolCalls.push(prefetched.call)
        finalToolResults.push(prefetched.result)
      }
      // A question about the trader's own chart reads it here, as the model
      // would call read_chart, so round 0 can answer (takeChartPrefetch).
      const chartRead = this.takeChartPrefetch(delivery, tools, indexedToolNames)
      if (chartRead) {
        currentMessages = [...currentMessages, ...chartRead.exchange]
        finalToolCalls.push(chartRead.call)
        finalToolResults.push(chartRead.result)
      }

      for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
        if (this.stopped(turn)) return
        const roundPage = this.resolvePage(panelTypes)
        if (roundPage !== page) {
          const refreshed = this.refreshLoopContext(systemPrompt, panelTypes, roundPage, tools)
          page = refreshed.page
          core = refreshed.core
          tools = refreshed.tools
          indexedToolNames = refreshed.indexedToolNames
          systemPrompt = refreshed.prompt
        }
        const streamResult = await this.streamChat({
          systemPrompt,
          stableHead,
          messages: currentMessages,
          // Only when the model asked to see the screen (view_screen).
          screenshot: screenRequested,
          tools: this.sentTools(delivery, core, tools),
          pageContext: page,
          debug: { round, feature: 'companion-chat' },
        })
        if (this.stopped(turn)) return
        lap(`stream round ${round + 1} done (${streamResult.toolCalls.length} tool calls)`)

        // No tool calls — this is the final text response
        if (streamResult.toolCalls.length === 0) {
          finalText = streamResult.text
          hitToolRoundLimit = false
          break
        }

        // Capture any text that came alongside the tool calls
        if (streamResult.text) {
          finalText = streamResult.text
        }

        // Execute tool calls. On fixed, a use_tool call is the call of the tool
        // it names, or refused here (roundCalls).
        const calls = this.roundCalls(delivery, streamResult.toolCalls, tools)
        this.noteRoundCalls(calls, tools)
        const toolStart = performance.now()
        const executed =
          calls.calls.length > 0
            ? await this.runRoundTools(calls.calls, turn, panels)
            : { results: [] as ToolResult[], screenshot: undefined }
        const toolResults = executed.results
        screenRequested = executed.screenshot
        if (this.stopped(turn)) return
        // Attribute this tool-execution time to the round that requested it.
        this._flushPendingDebugRound(performance.now() - toolStart)
        lap(
          `tools executed (${toolResults.filter((r: ToolResult) => r.success).length}/${toolResults.length} succeeded)`,
        )

        finalToolCalls.push(...calls.calls)
        finalToolResults.push(...toolResults)
        lastRound = { text: streamResult.text, calls: calls.calls, results: toolResults }
        this.updateSlot(toolResults)

        if (
          this._turnAccess === 'change' &&
          isResearchAndActRequest(text) &&
          calls.calls.some((call) => call.name === 'ask_analyst') &&
          toolResults.some((result) => !result.success)
        ) {
          finalText = RESEARCH_FAILURE_MESSAGE
          hitToolRoundLimit = false
          break
        }

        // Emit progress so chat UI can show "Tool (1)... Tool (2)..."
        this.events.emit({ type: 'tool-progress', count: finalToolCalls.length })

        // If get_tools was called, add the requested tools to the tools array for the next round
        this.loadRequestedTools(calls.calls, tools, indexedToolNames)

        // Terminal-tool passthrough: when the round's results are the finished
        // answer (passthroughAnswer), emit it verbatim and skip the redundant
        // follow-up "rephrase" LLM call (~2–4s + tokens, and it can mangle
        // citations). The voice path does the same.
        const passthrough = this.passthroughAnswer(text, calls, toolResults)
        if (passthrough !== null) {
          finalText = passthrough
          hitToolRoundLimit = false
          break
        }

        // Build follow-up messages: assistant's tool_use + user's tool_result,
        // the calls as the model made them, each answered.
        const answered = this.answeredResults(
          delivery,
          streamResult.toolCalls,
          toolResults,
          calls.rejected,
        )
        const assistantContent: ApiMessage = {
          role: 'assistant',
          content: [
            ...(streamResult.text ? [{ type: 'text' as const, text: streamResult.text }] : []),
            ...streamResult.toolCalls.map((tc: ToolCall) => ({
              type: 'tool_use' as const,
              id: tc.tool_use_id,
              name: tc.name,
              input: tc.input,
            })),
          ],
        }

        const toolResultContent: ApiMessage = {
          role: 'user',
          content: answered.map((tr: ToolResult) => ({
            type: 'tool_result' as const,
            tool_use_id: tr.tool_use_id,
            content: tr.content,
            is_error: tr.is_error,
          })),
        }

        currentMessages = [...currentMessages, assistantContent, toolResultContent]

        // If this was the last round and we still have tool calls, use what we have
        if (round === MAX_TOOL_ROUNDS - 1) {
          finalText = streamResult.text || ''
        }
      }

      if (hitToolRoundLimit)
        finalText = cappedTurnText(lastRound, { calls: finalToolCalls, results: finalToolResults })

      // ── Add assistant message ──
      // If no text was captured (tool-only response with no follow-up), generate a summary
      if (!finalText && finalToolCalls.length > 0) {
        finalText = INCOMPLETE_RESPONSE_MESSAGE
      }
      const { point, cleanText } = parsePointTag(finalText)
      // The "Do it" nudge: the request read as a change, but the check kept
      // the turn read-only, so the UI offers to re-send it able to act.
      const suggestChangeText = this._turnAccess === 'read' && this._offerChange ? text : undefined
      // What a tool parked this turn (a walkthrough) rides the answer (spec 6.2).
      const pending = this._pending
      const assistantMessage: Message = {
        role: 'assistant',
        text: cleanText,
        timestamp: Date.now(),
        source: 'chat',
        point,
        toolCalls: finalToolCalls.length > 0 ? finalToolCalls : undefined,
        toolResults: finalToolResults.length > 0 ? finalToolResults : undefined,
        suggestChangeText,
        ...(pending ? { pending } : {}),
      }
      this._messages.push(assistantMessage)
      this.events.emit({ type: 'message', message: assistantMessage })

      if (point) {
        this.events.emit({ type: 'point', data: point })
      }

      // Chat doesn't use TTS — go straight to idle
      if (this.isCurrentTurn(turn)) this.setState('idle')
      this.finishTurn(turn)
    } catch (err) {
      if (this.stopped(turn) || isAbortError(err)) {
        if (this.isCurrentTurn(turn)) this.setState('idle')
        this.finishTurn(turn)
        return
      }
      if (this.isCurrentTurn(turn)) {
        console.error('[chat] sendChatMessage error:', err)
        const error = err instanceof Error ? err : new Error(String(err))
        this._emitError(error)
        this.setState('idle')
      }
      this.finishTurn(turn)
    }
  }

  /**
   * Surface an error to the UI: emit an `error` event AND push a visible
   * assistant message so the failure never renders as a silent no-op. Shared by
   * the chat and voice paths — previously only the chat path pushed a message,
   * so a failed voice request cleared the spinner with nothing shown.
   */
  private _emitError(error: Error): void {
    if (this._turnStats) this._turnStats.errored = true
    this.events.emit({ type: 'error', error })
    const errorMessage: Message = {
      role: 'assistant',
      text: error instanceof AllowanceSpentError ? error.message : `Error: ${error.message}`,
      timestamp: Date.now(),
      source: 'chat',
    }
    this._messages.push(errorMessage)
    this.events.emit({ type: 'message', message: errorMessage })
  }

  /** Mark that TTS playback has started */
  startResponding(): void {
    if (this._state === 'processing') {
      this.setState('responding')
    }
  }

  /** Transition to idle (e.g. after TTS finishes) */
  finishResponding(): void {
    if (this._state === 'responding' || this._state === 'processing') {
      this.setState('idle')
    }
  }

  /** Mark the start of voice recording */
  startListening(): void {
    if (this._state === 'idle') {
      this.setState('listening')
    }
  }

  /** Cancel any in-flight request and return to idle */
  cancel(): void {
    // Barge-in: stop sentence-streamed speech (duplex order: sink first).
    this._config.directDataRoute?.cancelSpeech?.()
    this.turnAbortController?.abort()
    this.turnAbortController = null
    this.closeTurnStats('stopped')
    this.turnId = ''
    activeClientTurnId = null
    activeTurnModality = null
    this.setTurnToken(null)
    this.abortController?.abort()
    this.abortController = null
    this.setState('idle')
  }

  /** Clear conversation history */
  clearHistory(): void {
    this._messages = []
    this._lastTurnActed = false
    this.setPending(null)
    this._outcomeNotes = []
  }

  /**
   * Replace the in-memory history wholesale (thread restore/select). No
   * events are emitted — the caller owns its UI state; the point is that the
   * MODEL sees the restored turns (they feed the prompt via the recent-turns
   * window). Never call mid-turn: swapping history under a running tool loop
   * would corrupt it; hosts gate on the processing state. The thread's own
   * pending slot comes with it, with no event either (spec 6.2): what its last
   * answer left waiting.
   */
  loadMessages(messages: Message[]): void {
    this._messages = [...messages]
    this._lastTurnActed = false
    this._pending = restoredPending(this._messages)
    this._outcomeNotes = []
  }

  /** Clean up resources */
  destroy(): void {
    this.cancel()
    this.events.clear()
    this.context.clear()
  }

  // ── Private helpers ────────────────────────────────────────

  private async classifyIntent(
    text: string,
    panelTypes: string[],
    page?: PageContext,
  ): Promise<TurnClassification> {
    // Local, zero-latency routing — replaces the classify LLM round (~1.4-2.7s
    // of pure time-to-first-word tax). The turn's access is the source of truth for
    // action-vs-guidance (needs_actions); needs_image + the backtest-only intent
    // come from keyword heuristics; panel = the open panel the question NAMES
    // (null when none is named — the model then resolves from the layout or
    // asks via request_panel_selection).
    const t = text.toLowerCase()
    const needs_actions = this._turnAccess === 'change'
    // Screenshot only when the user points at the screen itself. The old
    // trigger fired on "why is", "looks" and "currently", so most voice turns
    // paid a full-page capture (~5 s on a big dashboard, 2026-10-02) for an
    // image the answer never needed. Panel contents reach the model as data.
    const needs_image =
      /\b(on (my|the|this) screen|what('?s| is) (showing|on (my|the|this) screen)|(do|can) you see|look(ing)? at (this|my|the) (screen|chart|picture|image)|screenshot|highlighted|selected)\b/.test(
        t,
      )
    let intent: string | null = null
    if (
      needs_actions &&
      (page === 'backtest' || /\b(backtest|strateg(?:y|ies)|node graph)\b/.test(t))
    ) {
      if (/\b(build|create|make|new|set up|add|generate)\b/.test(t)) intent = 'create'
      else if (
        /\b(change|edit|modify|adjust|tighten|loosen|fix|improve|update|tune|remove|swap)\b/.test(t)
      )
        intent = 'modify'
      else intent = 'read'
    }
    // Platform how-to detector — drives a per-turn "call get_platform_guide
    // FIRST" nudge. The standing rule in the base prompt gets skipped when the
    // model feels confident (2026-08-25: improvised chart *styles* for a chart
    // *layout* question); a turn-specific instruction is much harder to ignore.
    const looks_howto =
      /\b(how (do|can|would|should) i\b|how to\b|where (is|are|do|can)\b|what does\b|settings?|configure|c[oó]mo (puedo|se)\b|d[oó]nde\b)/i.test(
        t,
      )
    return {
      panel: inferPanelTypeFromText(text, panelTypes),
      needs_image,
      needs_actions,
      intent,
      looks_howto,
    }
  }

  /**
   * The system prompt of a chat-model turn, typed or spoken (2026-10-22: voice
   * turns were built from the May voice base prompt, with a 75-word cap, all
   * lowercase, no follow-ups and none of chat's grounding rules, and answered
   * thinner than chat). Both paths build it here. A spoken turn differs in its
   * answer style, which takes the place of the cashtag rule, and in what it
   * sees of the screen (`screen`: the screenshot note and the panel layout).
   *
   * Assembly order is the prompt cache's (2026-10-19): the STABLE head comes
   * first, the same bytes on every round and turn for this trader and
   * modality (identity, language, answer style, column keys, persona framing,
   * host extension). The server keys its prompt cache on it and ends an
   * Anthropic model's cache breakpoint there (`stableHead`, sent as
   * system_prompt_stable_chars), so nothing that changes per turn may go above
   * it. Then what holds for a turn and the next while the access, page and
   * panels hold (principles, tool index, tool context, scope extras), then the
   * per-turn VOLATILE content (date, page, intent, panel inventory, this
   * message's hints). What the trader undid since the last reply closes it.
   */
  private buildTurnPrompt(turn: {
    modality: 'text' | 'voice'
    text: string
    page: PageContext
    panels: ReturnType<typeof collectPanelLayout>
    panelTypes: string[]
    /** The turn's always-loaded and indexed tool names. */
    activeToolNames: string[]
    classify: TurnClassification
    /** A spoken turn's view of the screen, after the panel inventory. */
    screen?: string[]
  }): { systemPrompt: string; stableHead: string } {
    const style =
      turn.modality === 'voice'
        ? `\n${this._config.voiceStyle ?? DEFAULT_SPOKEN_STYLE}`
        : CASHTAG_RULE
    let prompt = `you are Edge AI, a co-worker inside the Edge trading platform.
${this.languageRule(`ALWAYS respond in ${this._language}, regardless of what language the user's message or any tool result is in (translate tool output to ${this._language} if needed).`)} tool names and column keys stay in English in tool calls, and never appear in what you say to the trader: say the measure in words (relative volume, float, change %). call set_language only when the user asks to change the reply language.
${style}

${COLUMN_KEYS}`

    // Persona framing is stable: it changes only when the host sets it.
    if (this._personaFraming) {
      prompt += `\n\n${this._personaFraming}`
    }
    if (this._config.systemPromptExtension) {
      prompt += `\n\n${this._config.systemPromptExtension}`
    }
    const stableHead = prompt

    // Whether this turn may act: the turn check decides it per turn.
    prompt += `\n\n${this._turnAccess === 'read' ? READ_PRINCIPLES : CHANGE_PRINCIPLES}`

    // Tool index: text list of available tools (full schemas loaded on demand via get_tools)
    const toolIndex = getToolIndex(turn.panelTypes, turn.page, this._turnAccess, this.toolFilter())
    if (toolIndex) {
      prompt += `\n\n${toolIndex}`
    }

    // Tool-specific context (e.g. the backtest node catalog), gated on the
    // indexed tool names too, since the always-loaded list never includes them.
    prompt = appendToolContext(prompt, turn.activeToolNames)

    // Append any scope-specific prompt extras (e.g. backtest action principle)
    const scope = getRegisteredScope(turn.page)
    if (this._turnAccess === 'change' && scope?.promptExtras) {
      prompt += `\n\n${scope.promptExtras}`
    }

    // ── Volatile, per-turn content below this line ──

    prompt += `\n\n${currentDateContext()}`

    // Current page: without this the model has no idea WHERE the user is
    // (panel-less pages like the backtest editor produce an empty inventory),
    // and "build X" prompts have triggered needless navigate_to hops away
    // from the very page that has the right tools (2026-07-23).
    prompt += `\n\n## current page\n${turn.page} — the user is already here; only navigate_to a route when they ask to go somewhere else.`

    // Cookbook few-shot exemplars, injected only on strategy-generation
    // intent (create + strategy-build tool present); zero cost otherwise.
    // They follow this message's text, so they sit in the volatile part.
    prompt = appendGroundingContext(prompt, {
      toolNames: turn.activeToolNames,
      intent: turn.classify.intent,
      intentText: turn.text,
    })

    // Inject classify intent; never steer toward creation on a read-only turn.
    // The "add_panel FIRST" guidance is panel-workspace specific: gate it on
    // add_panel actually being an active tool. On the panel-less backtest
    // editor "build me a strategy" is also intent:create, but the build tool
    // is create_strategy_from_recipe / apply_graph_ops (see tool-registry);
    // injecting add_panel there sent the model chasing that tool back to the
    // dashboard workspace, a spurious navigate_to /backtest (2026-07-23).
    if (
      this._turnAccess !== 'read' &&
      turn.classify.intent === 'create' &&
      turn.activeToolNames.includes('add_panel')
    ) {
      prompt += `\n\n## intent: CREATE\ncall add_panel FIRST, then configure using the returned panel_id.`
    }

    // Panel inventory: the model needs panel IDs to target specific panels
    if (turn.panels.length > 0) {
      const inventory = turn.panels.map((p) => `${p.panelType} (${p.panelId})`).join(', ')
      prompt += `\n\n## current workspace\npanels: ${inventory}`
    }

    // A panel the trader names that is not open gets the analyst's rule here
    // too (named-panels.ts): no scanner on the layout once sent the model
    // after one that could not exist (2026-10-18).
    const notOpen = missingNamedPanels(turn.text, turn.panelTypes)
    if (notOpen.length > 0) prompt += `\n\n${missingPanelSection(notOpen)}`

    for (const block of turn.screen ?? []) prompt += `\n\n${block}`

    // Labeled interactive elements, right after the panel inventory
    const labeledDesc = describeLabeledElements(collectLabeledElements(turn.panels))
    if (labeledDesc) {
      prompt += `\n\n${labeledDesc}`
    }
    if (turn.classify.looks_howto) {
      // Principle 3 is the how-to fallback on a read-only turn only; on a turn
      // that may act it is '"new" means create', so the hint says the fallback.
      const fallback =
        this._turnAccess === 'read'
          ? "follow principle 3's fallback (useful answer, no invented UI)"
          : "say in one plain line that you don't have a guide on that yet, then give the most useful answer you can without inventing UI"
      prompt += `\n\n## this turn\nthis message looks like a platform how-to question. ground it FIRST with get_platform_guide${turn.classify.panel ? ` (panel: "${turn.classify.panel}")` : ''} and answer from what comes back — do not invent steps from memory. if nothing covers it, ${fallback}. set wants_to_do_it when they want to do it now (how do I, show me), not when they ask whether something is possible or how it works.`
    }

    prompt += this.takeOutcomeNotes()
    return { systemPrompt: prompt, stableHead }
  }

  /**
   * Whether a round's results may be the turn's answer at all: never when a
   * use_tool call was refused, and never on a research-and-act turn, whose
   * research feeds the change.
   */
  private mayPassThrough(text: string, calls: { rejected: RefusedCall[] }): boolean {
    if (calls.rejected.length > 0) return false
    return !(this._turnAccess === 'change' && isResearchAndActRequest(text))
  }

  /**
   * The round's finished answer when its results are the answer, so no model
   * round follows to rephrase or condense it (terminalPassthrough): every call
   * is a terminal-answer tool that succeeded (ask_analyst, summarize_history)
   * or its result is flagged passthrough (a launched walkthrough's intro, a
   * fixed friendly failure such as the spent allowance), in a round that
   * mayPassThrough. Null: the model answers from the results. Typed and
   * spoken turns alike (2026-10-22).
   */
  private passthroughAnswer(
    text: string,
    calls: { calls: ToolCall[]; rejected: RefusedCall[] },
    results: ToolResult[],
  ): string | null {
    return this.mayPassThrough(text, calls) ? terminalPassthrough(calls.calls, results) : null
  }

  private getHistoryForApi(maxTurns: number): ApiMessage[] {
    // Take last N pairs of messages (user + assistant). Acknowledgement lines are
    // status, not conversation: they go before the window is counted, so they do
    // not use it up.
    const recent = this._messages.filter((m) => !isAck(m)).slice(-(maxTurns * 2))
    return recent.map((m) => {
      // For history, send text-only versions of all messages.
      // Tool calls were executed on the frontend in a single turn —
      // including tool_use/tool_result blocks in follow-up turns
      // creates malformed API payloads. The text portion captures
      // what Claude said (e.g., "I added a scanner for you") which
      // is sufficient context for the next turn.
      return { role: m.role, text: m.text }
    })
  }

  private async streamChat(params: {
    systemPrompt: string
    /**
     * The prompt's stable head (prompt caching, 2026-10-19): sent as how many
     * leading characters are the same on every round and turn, so the server
     * can key its prompt cache on them.
     */
    stableHead?: string
    messages: ApiMessage[]
    screenshot?: string
    tools?: ToolDefinition[]
    pageContext?: string
    /** Dev profiling context. When present (and a debug observer is attached),
     *  this round is reported to the debug sidebar. Omitted = zero overhead. */
    debug?: { round: number; feature: 'companion-chat' | 'companion-voice' }
  }): Promise<StreamResult> {
    const roundAbortController = new AbortController()
    const removeTurnAbort = relayAbortSignal(this.turnAbortController?.signal, roundAbortController)
    this.abortController = roundAbortController

    // Every round of one user turn shares the turn's id in the debug view (beginTurn).
    const debugTurnId = this._debugTurnId

    // Safety net: if a prior tool round deferred and the caller never flushed
    // it (exotic break path), emit it now without tool timing so no round is
    // ever silently dropped. Normal flow already flushed it with real toolMs.
    if (params.debug) this._flushPendingDebugRound(undefined)

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (this._config.getAuthToken) {
      const token = await this._config.getAuthToken()
      headers['Authorization'] = `Bearer ${token}`
    }

    const body: Record<string, unknown> = {
      messages: params.messages,
      system_prompt: params.systemPrompt,
      screenshot: params.screenshot,
      model: this._config.model,
    }
    if (this._persona) {
      body.persona = this._persona
    }
    if (params.pageContext) {
      body.page_context = params.pageContext
    }
    if (params.tools?.length) {
      body.tools = params.tools
    }
    if (this.turnToken) {
      body.turn_token = this.turnToken
    }
    if (this._sessionId) {
      body.session_id = this._sessionId
    }
    if (this.turnId) {
      body.client_turn_id = this.turnId
    }
    if (params.stableHead && params.systemPrompt.startsWith(params.stableHead)) {
      body.system_prompt_stable_chars = params.stableHead.length
    }

    const chatStart = performance.now()
    let ttftMs: number | undefined
    let fullText = ''
    const toolCalls: ToolCall[] = []
    let ok = true
    let errorMsg: string | undefined
    // Populated from the backend's terminal `usage` event (tokens + real cost).
    let usage:
      | {
          tokensIn?: number
          tokensOut?: number
          cachedTokens?: number
          costUsd?: number
          modelServed?: string
        }
      | undefined

    try {
      const response = await fetch(`${this._config.apiBase}/chat`, {
        method: 'POST',
        headers,
        signal: roundAbortController.signal,
        body: JSON.stringify(body),
      })
      console.log(
        `[companion:timing] chat fetch response: ${(performance.now() - chatStart).toFixed(0)}ms`,
      )

      if (!response.ok) {
        if (response.status === 402) {
          // A spent allowance is the trader's situation, not an error code: the
          // server's sentence is shown as it is (AI metered at real cost, 2026-10-16).
          const body = (await response.json().catch(() => null)) as { detail?: unknown } | null
          throw new AllowanceSpentError(
            typeof body?.detail === 'string' ? body.detail : ALLOWANCE_SPENT_MESSAGE,
          )
        }
        throw new Error(`Companion chat failed: ${response.status} ${response.statusText}`)
      }

      // Parse SSE stream (sse-starlette uses \r\n line endings)
      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let buffer = ''
      let firstToken = true

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Shared SSE frame parser (drainFramesFromBuffer + parseSseFrame handle
        // the \r\n\r\n / \n\n boundary and JSON parsing) — one implementation
        // across the companion loop and the frontend streaming consumers.
        const { frames, remaining } = drainFramesFromBuffer(buffer)
        buffer = remaining

        for (const frame of frames) {
          const parsed = parseSseFrame(frame)
          if (!parsed) continue
          const eventType = parsed.event
          const data = parsed.data as {
            text?: string
            tool_use_id?: string
            name?: string
            input?: Record<string, unknown>
            message?: string
          }

          if (eventType === 'text_delta') {
            this.noteFirstText()
            if (firstToken) {
              ttftMs = performance.now() - chatStart
              console.log(`[companion:timing] chat TTFB: ${ttftMs.toFixed(0)}ms`)
              firstToken = false
            }
            fullText += data.text
            // Relay the token live so the UI can render a "typing" bubble
            // instead of waiting for the whole turn. The final `message` event
            // still carries the canonical text.
            this.events.emit({ type: 'text-delta', text: data.text ?? '', source: 'companion' })
          } else if (eventType === 'text_reset') {
            // The server withdrew the finished answer to say a database key in
            // words (HARD RULE); the replacement follows as text_delta.
            fullText = ''
            this.events.emit({ type: 'text-reset', source: 'companion' })
          } else if (eventType === 'turn') {
            const token = (parsed.data as { turn_token?: unknown }).turn_token
            if (typeof token === 'string' && token) this.setTurnToken(token)
          } else if (eventType === 'tool_use_end') {
            // Complete tool call received from backend
            toolCalls.push({
              tool_use_id: data.tool_use_id ?? '',
              name: data.name ?? '',
              input: data.input ?? {},
            })
          } else if (eventType === 'usage') {
            // Terminal usage event (tokens + real dollar cost). Captured for the
            // debug bridge; the chat UI ignores it.
            const u = parsed.data as {
              prompt_tokens?: number
              completion_tokens?: number
              cached_tokens?: number
              cost?: number
              model?: string
            }
            usage = {
              tokensIn: u.prompt_tokens,
              tokensOut: u.completion_tokens,
              cachedTokens: u.cached_tokens,
              costUsd: typeof u.cost === 'number' ? u.cost : undefined,
              modelServed: u.model,
            }
          } else if (eventType === 'error') {
            throw new Error(data.message)
          }
          // tool_use_start is informational — we wait for tool_use_end with full input
        }
      }

      if (this.abortController === roundAbortController) this.abortController = null
      return { text: fullText, toolCalls }
    } catch (err) {
      ok = false
      errorMsg = err instanceof Error ? err.message : String(err)
      throw err
    } finally {
      removeTurnAbort()
      if (this.abortController === roundAbortController) this.abortController = null
      this.noteChatRound(usage)
      // Report this round to the dev debug bridge. A round that requested tools
      // is DEFERRED (stored as pending) so the caller can attach tool-execution
      // time to it; the final answer round and any errored round emit right
      // away (no tools follow). Wrapped so a buggy observer never breaks chat.
      if (params.debug) {
        const draft: Omit<CompanionDebugRound, 'toolMs'> = {
          conversationId: this.conversationId,
          turnId: debugTurnId,
          round: params.debug.round,
          feature: params.debug.feature,
          modelRequested: this._config.model,
          ttftMs,
          totalMs: performance.now() - chatStart,
          toolCalls: toolCalls.map((tc) => ({ name: tc.name })),
          input: { systemPrompt: params.systemPrompt, messages: params.messages },
          output: { text: fullText },
          modelServed: usage?.modelServed,
          tokensIn: usage?.tokensIn,
          tokensOut: usage?.tokensOut,
          cachedTokens: usage?.cachedTokens,
          costUsd: usage?.costUsd,
          ok,
          error: errorMsg,
        }
        if (ok && draft.toolCalls.length > 0) {
          this._pendingDebugRound = draft
        } else {
          this._pendingDebugRound = null
          if (this.debugRoundListener) {
            try {
              this.debugRoundListener({ ...draft, toolMs: undefined })
            } catch (emitErr) {
              console.warn('[companion:debug] round emit failed', emitErr)
            }
          }
        }
      }
    }
  }
}
